import { useState, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Shield, TrendingDown, DollarSign, BarChart3, Target, Flame, FileDown, Coins, Grid3x3, Mail, Loader2, Globe } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, Area, AreaChart, BarChart, Bar, Cell,
  Treemap, ScatterChart, Scatter, ZAxis, PieChart, Pie,
} from 'recharts';
import { HedgePosition, HedgeAnalysis, estimateVolatility, estimateOptionPremium } from '@/lib/hedgeAnalysis';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface Props {
  analyses: HedgeAnalysis[];
  positions: HedgePosition[];
  totalPortfolioValue: number;
}

// ─── Currency conversion (simplified) ─────────────────────────────
const USD_CHF = 0.88;

function toChf(usd: number) { return usd * USD_CHF; }
function fmt(v: number, currency = 'USD') {
  const abs = Math.abs(v);
  const s = currency === 'CHF' ? 'CHF ' : '$';
  const formatted = abs >= 1e6
    ? `${s}${(abs / 1e6).toFixed(2)}M`
    : abs >= 1e3
    ? `${s}${abs.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
    : `${s}${abs.toFixed(2)}`;
  return v < 0 ? `-${formatted}` : formatted;
}

// ─── Protection tiers ──────────────────────────────────────────────

interface ProtectionTier {
  id: string;
  label: string;
  description: string;
  otmPct: number;     // 0 = ATM, 0.05 = 5% OTM, etc
  daysToExpiry: number;
  color: string;
}

const PROTECTION_TIERS: ProtectionTier[] = [
  { id: 'conservative', label: 'Conservative (ATM, 90d)', description: 'Full protection at current price. Highest cost but best coverage.', otmPct: 0, daysToExpiry: 90, color: '#22c55e' },
  { id: 'balanced', label: 'Balanced (5% OTM, 60d)', description: 'Accept 5% drawdown before protection kicks in. Moderate cost.', otmPct: 0.05, daysToExpiry: 60, color: '#3b82f6' },
  { id: 'budget', label: 'Budget (10% OTM, 45d)', description: 'Accept 10% drawdown. Low cost but larger gap before protection.', otmPct: 0.10, daysToExpiry: 45, color: '#f59e0b' },
  { id: 'crash', label: 'Crash Insurance (15% OTM, 30d)', description: 'Only protects against severe crashes. Very cheap, short duration.', otmPct: 0.15, daysToExpiry: 30, color: '#ef4444' },
];

function computeTierCost(positions: HedgePosition[], tier: ProtectionTier) {
  let totalCostUSD = 0;
  const details: { ticker: string; strike: number; premium: number; contracts: number; cost: number; currency: string }[] = [];
  
  for (const p of positions) {
    const vol = estimateVolatility(p.gainPct);
    const strikePct = 1 - tier.otmPct;
    const strike = p.currentPrice * strikePct;
    const premium = estimateOptionPremium(p.currentPrice, strike, vol, tier.daysToExpiry, 'put');
    const contracts = Math.ceil(p.quantity / 100);
    const cost = premium * contracts * 100;
    totalCostUSD += cost;
    details.push({ ticker: p.ticker, strike: Math.round(strike * 100) / 100, premium: Math.round(premium * 100) / 100, contracts, cost: Math.round(cost * 100) / 100, currency: p.localCurrency });
  }
  return { totalCostUSD: Math.round(totalCostUSD * 100) / 100, details };
}

// ─── Protection cost vs market drop chart data ─────────────────────

function generateCostVsDropData(positions: HedgePosition[], daysToExpiry: number) {
  const data = [];
  for (let otm = 0; otm <= 25; otm += 1) {
    let costUSD = 0;
    let hedgedValueUSD = 0;
    for (const p of positions) {
      const vol = estimateVolatility(p.gainPct);
      const strikePct = 1 - otm / 100;
      const strike = p.currentPrice * strikePct;
      const premium = estimateOptionPremium(p.currentPrice, strike, vol, daysToExpiry, 'put');
      const contracts = Math.ceil(p.quantity / 100);
      costUSD += premium * contracts * 100;
      hedgedValueUSD += strike * p.quantity;
    }
    data.push({
      otm,
      costUSD: Math.round(costUSD),
      costCHF: Math.round(costUSD * USD_CHF),
      hedgedUSD: Math.round(hedgedValueUSD),
      hedgedCHF: Math.round(hedgedValueUSD * USD_CHF),
      costPct: Math.round((costUSD / positions.reduce((s, p) => s + p.totalValue, 0)) * 10000) / 100,
    });
  }
  return data;
}

// ─── Loss/Profit simulation table data ─────────────────────────────

function generateSimulationData(positions: HedgePosition[], tierOtmPct: number, daysToExpiry: number) {
  const totalValue = positions.reduce((s, p) => s + p.totalValue, 0);
  
  // compute hedge cost
  let hedgeCostUSD = 0;
  const strikes: { strike: number; qty: number }[] = [];
  for (const p of positions) {
    const vol = estimateVolatility(p.gainPct);
    const strikePct = 1 - tierOtmPct;
    const strike = p.currentPrice * strikePct;
    const premium = estimateOptionPremium(p.currentPrice, strike, vol, daysToExpiry, 'put');
    const contracts = Math.ceil(p.quantity / 100);
    hedgeCostUSD += premium * contracts * 100;
    strikes.push({ strike, qty: p.quantity });
  }

  const rows = [];
  for (let dropPct = -30; dropPct <= 30; dropPct += 5) {
    // unhedged P&L
    const unhedgedPL = totalValue * (dropPct / 100);
    
    // hedged P&L: for drops, put payoff kicks in below strike
    let putPayoff = 0;
    positions.forEach((p, i) => {
      const newPrice = p.currentPrice * (1 + dropPct / 100);
      const payoff = Math.max(strikes[i].strike - newPrice, 0) * p.quantity;
      putPayoff += payoff;
    });
    
    const hedgedPL = unhedgedPL + putPayoff - hedgeCostUSD;
    const netBenefit = hedgedPL - unhedgedPL;

    rows.push({
      marketMove: dropPct,
      unhedgedPL_USD: Math.round(unhedgedPL),
      unhedgedPL_CHF: Math.round(unhedgedPL * USD_CHF),
      hedgedPL_USD: Math.round(hedgedPL),
      hedgedPL_CHF: Math.round(hedgedPL * USD_CHF),
      putPayoff_USD: Math.round(putPayoff),
      hedgeCost_USD: Math.round(hedgeCostUSD),
      netBenefit_USD: Math.round(netBenefit),
      netBenefit_CHF: Math.round(netBenefit * USD_CHF),
    });
  }
  return rows;
}

// ─── Option recommendation per position ────────────────────────────

function generateOptionRecommendations(positions: HedgePosition[], tier: ProtectionTier) {
  return positions.map(p => {
    const vol = estimateVolatility(p.gainPct);
    const strikePct = 1 - tier.otmPct;
    const strike = p.currentPrice * strikePct;
    const premium = estimateOptionPremium(p.currentPrice, strike, vol, tier.daysToExpiry, 'put');
    const contracts = Math.ceil(p.quantity / 100);
    const totalCost = premium * contracts * 100;
    const maxProtected = (p.currentPrice - strike) * p.quantity;
    
    return {
      ticker: p.ticker,
      stockName: p.stockName,
      currentPrice: p.currentPrice,
      currency: p.localCurrency,
      quantity: p.quantity,
      optionType: 'PUT' as const,
      strike: Math.round(strike * 100) / 100,
      expiry: `${tier.daysToExpiry}d`,
      premiumPerShare: Math.round(premium * 100) / 100,
      contracts,
      totalCost: Math.round(totalCost * 100) / 100,
      deductible: Math.round(maxProtected * 100) / 100,
      protectedBelow: Math.round(strike * 100) / 100,
    };
  });
}

// ─── Index ETF Hedge Definitions ───────────────────────────────────

interface IndexETF {
  ticker: string;
  name: string;
  currentPrice: number;
  volatility: number;
  description: string;
  color: string;
}

const INDEX_ETFS: IndexETF[] = [
  { ticker: 'SPY', name: 'S&P 500 ETF', currentPrice: 580, volatility: 0.18, description: 'Broad US market. Best for diversified US portfolios.', color: '#3b82f6' },
  { ticker: 'QQQ', name: 'Nasdaq-100 ETF', currentPrice: 505, volatility: 0.24, description: 'Tech-heavy. Best for tech-weighted portfolios.', color: '#8b5cf6' },
  { ticker: 'IWM', name: 'Russell 2000 ETF', currentPrice: 210, volatility: 0.22, description: 'Small caps. Higher vol, wider tail risk coverage.', color: '#f59e0b' },
  { ticker: 'EFA', name: 'MSCI EAFE ETF', currentPrice: 82, volatility: 0.16, description: 'International developed markets. Best for non-US exposure.', color: '#06b6d4' },
];

interface IndexHedgeTier {
  id: string;
  label: string;
  otmPct: number;
  daysToExpiry: number;
  description: string;
}

const INDEX_HEDGE_TIERS: IndexHedgeTier[] = [
  { id: 'tight', label: 'Tight (ATM, 90d)', otmPct: 0, daysToExpiry: 90, description: 'Maximum protection. Highest cost.' },
  { id: 'standard', label: 'Standard (5% OTM, 60d)', otmPct: 0.05, daysToExpiry: 60, description: 'Accept 5% drawdown. Balanced cost.' },
  { id: 'wide', label: 'Wide (10% OTM, 45d)', otmPct: 0.10, daysToExpiry: 45, description: 'Accept 10% before kick-in. Low cost.' },
  { id: 'tail', label: 'Tail Risk (20% OTM, 30d)', otmPct: 0.20, daysToExpiry: 30, description: 'Black swan protection only. Very cheap.' },
];

function estimatePortfolioBeta(positions: HedgePosition[]): number {
  // Heuristic: tech stocks have higher beta, defensive stocks lower
  let weightedBeta = 0;
  const totalValue = positions.reduce((s, p) => s + p.totalValue, 0);
  for (const p of positions) {
    const sector = detectSector(p.stockName);
    const w = p.totalValue / totalValue;
    let beta = 1.0;
    if (sector === 'Technology') beta = 1.25;
    else if (sector === 'Healthcare') beta = 0.85;
    else if (sector === 'Finance') beta = 1.15;
    else if (sector === 'Consumer') beta = 0.90;
    else if (sector === 'Energy') beta = 1.10;
    else if (sector === 'Utilities') beta = 0.60;
    else if (sector === 'RealEstate') beta = 0.75;
    else if (sector === 'Materials') beta = 1.05;
    else if (sector === 'Industrial') beta = 1.00;
    else if (sector === 'Telecom') beta = 0.80;
    // Adjust for gain volatility
    const absGain = Math.abs(p.gainPct);
    if (absGain > 0.5) beta *= 1.15;
    else if (absGain > 0.3) beta *= 1.08;
    weightedBeta += w * beta;
  }
  return Math.round(weightedBeta * 100) / 100;
}

function computeIndexHedge(
  positions: HedgePosition[],
  etf: IndexETF,
  tierOtmPct: number,
  daysToExpiry: number,
) {
  const totalValue = positions.reduce((s, p) => s + p.totalValue, 0);
  const beta = estimatePortfolioBeta(positions);
  
  // Notional = portfolio value * beta (adjusts for portfolio sensitivity to index)
  const notional = totalValue * beta;
  const contractsNeeded = Math.ceil(notional / (etf.currentPrice * 100));
  
  const strike = etf.currentPrice * (1 - tierOtmPct);
  const premium = estimateOptionPremium(etf.currentPrice, strike, etf.volatility, daysToExpiry, 'put');
  const totalCost = premium * contractsNeeded * 100;
  const costPct = (totalCost / totalValue) * 100;
  
  // Simulation: for various market drops, what's the payoff?
  const simulation = [];
  for (let dropPct = -30; dropPct <= 10; dropPct += 5) {
    const portfolioMove = dropPct / 100 * beta; // beta-adjusted
    const unhedgedPL = totalValue * (dropPct / 100);
    const newIndexPrice = etf.currentPrice * (1 + dropPct / 100);
    const putPayoff = Math.max(strike - newIndexPrice, 0) * contractsNeeded * 100;
    const hedgedPL = unhedgedPL + putPayoff - totalCost;
    simulation.push({
      marketMove: dropPct,
      unhedgedPL: Math.round(unhedgedPL),
      putPayoff: Math.round(putPayoff),
      hedgedPL: Math.round(hedgedPL),
      netBenefit: Math.round(hedgedPL - unhedgedPL),
    });
  }
  
  return {
    etf,
    beta,
    notional: Math.round(notional),
    contractsNeeded,
    strike: Math.round(strike * 100) / 100,
    premiumPerShare: Math.round(premium * 100) / 100,
    totalCost: Math.round(totalCost * 100) / 100,
    costPct: Math.round(costPct * 100) / 100,
    simulation,
  };
}

// ─── Index Hedge Sub-component ─────────────────────────────────────

function IndexHedgePanel({ positions, totalPortfolioValue }: { positions: HedgePosition[]; totalPortfolioValue: number }) {
  const [selectedEtfIdx, setSelectedEtfIdx] = useState(0);
  const [selectedTierIdx, setSelectedTierIdx] = useState(1);
  
  const beta = useMemo(() => estimatePortfolioBeta(positions), [positions]);
  
  const allResults = useMemo(() => {
    const tier = INDEX_HEDGE_TIERS[selectedTierIdx];
    return INDEX_ETFS.map(etf => computeIndexHedge(positions, etf, tier.otmPct, tier.daysToExpiry));
  }, [positions, selectedTierIdx]);
  
  const selectedResult = allResults[selectedEtfIdx];
  const tier = INDEX_HEDGE_TIERS[selectedTierIdx];
  
  // Compare all ETFs at selected tier
  const comparisonData = allResults.map(r => ({
    name: r.etf.ticker,
    cost: r.totalCost,
    costCHF: Math.round(r.totalCost * USD_CHF),
    contracts: r.contractsNeeded,
    costPct: r.costPct,
  }));
  
  // Compare index vs individual stock hedges
  const individualCost = useMemo(() => {
    return computeTierCost(positions, {
      id: 'idx-compare',
      label: '',
      description: '',
      otmPct: tier.otmPct,
      daysToExpiry: tier.daysToExpiry,
      color: '',
    }).totalCostUSD;
  }, [positions, tier]);

  return (
    <div className="space-y-4">
      {/* Portfolio Beta */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-[10px] text-muted-foreground uppercase">Est. Portfolio Beta</p>
            <p className="text-2xl font-bold text-primary">{beta.toFixed(2)}</p>
            <p className="text-[10px] text-muted-foreground">{beta > 1.1 ? 'Above market' : beta < 0.9 ? 'Defensive' : 'Market-like'}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-[10px] text-muted-foreground uppercase">Index Hedge Cost</p>
            <p className="text-lg font-bold text-destructive">{fmt(selectedResult.totalCost)}</p>
            <p className="text-[10px] text-muted-foreground">{selectedResult.costPct}% of portfolio</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-[10px] text-muted-foreground uppercase">Individual Hedge Cost</p>
            <p className="text-lg font-bold text-destructive">{fmt(individualCost)}</p>
            <p className="text-[10px] text-muted-foreground">{((individualCost / totalPortfolioValue) * 100).toFixed(2)}%</p>
          </CardContent>
        </Card>
        <Card className="border-primary/30">
          <CardContent className="p-3 text-center">
            <p className="text-[10px] text-muted-foreground uppercase">Index Saving vs Individual</p>
            {(() => {
              const saving = individualCost - selectedResult.totalCost;
              const savingPct = individualCost > 0 ? (saving / individualCost) * 100 : 0;
              return (
                <>
                  <p className={`text-lg font-bold ${saving > 0 ? 'text-green-600 dark:text-green-400' : 'text-destructive'}`}>
                    {saving > 0 ? '-' : '+'}{fmt(Math.abs(saving))}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    {saving > 0 ? `${savingPct.toFixed(0)}% cheaper` : `${Math.abs(savingPct).toFixed(0)}% more expensive`}
                  </p>
                </>
              );
            })()}
          </CardContent>
        </Card>
      </div>

      {/* ETF Selector */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Globe className="h-4 w-4" />
            Select Index ETF & Protection Tier
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {INDEX_ETFS.map((etf, i) => (
              <button
                key={etf.ticker}
                onClick={() => setSelectedEtfIdx(i)}
                className={`p-3 rounded-lg border text-left transition-all ${
                  i === selectedEtfIdx
                    ? 'border-primary bg-primary/10 ring-1 ring-primary'
                    : 'border-border hover:border-muted-foreground/30'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: etf.color }} />
                  <span className="text-xs font-bold text-foreground">{etf.ticker}</span>
                </div>
                <p className="text-[10px] text-muted-foreground">{etf.name}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{etf.description}</p>
                <p className="text-sm font-bold mt-1" style={{ color: etf.color }}>
                  ${etf.currentPrice} | IV: {(etf.volatility * 100).toFixed(0)}%
                </p>
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {INDEX_HEDGE_TIERS.map((t, i) => (
              <button
                key={t.id}
                onClick={() => setSelectedTierIdx(i)}
                className={`p-2 rounded-lg border text-left transition-all text-xs ${
                  i === selectedTierIdx
                    ? 'border-primary bg-primary/10 ring-1 ring-primary'
                    : 'border-border hover:border-muted-foreground/30'
                }`}
              >
                <span className="font-semibold text-foreground">{t.label}</span>
                <p className="text-[10px] text-muted-foreground mt-0.5">{t.description}</p>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Selected Index Hedge Detail */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">
            {selectedResult.etf.ticker} PUT — {tier.label}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="border-b-2 border-border">
                  <th className="text-left py-2 px-2 font-semibold text-foreground">ETF</th>
                  <th className="text-right py-2 px-2 font-semibold text-foreground">Spot</th>
                  <th className="text-right py-2 px-2 font-semibold text-foreground">Strike</th>
                  <th className="text-right py-2 px-2 font-semibold text-foreground">Beta</th>
                  <th className="text-right py-2 px-2 font-semibold text-foreground">Notional</th>
                  <th className="text-right py-2 px-2 font-semibold text-foreground">Contracts</th>
                  <th className="text-right py-2 px-2 font-semibold text-foreground">Prem/sh</th>
                  <th className="text-right py-2 px-2 font-semibold text-foreground">Total Cost</th>
                  <th className="text-right py-2 px-2 font-semibold text-foreground">Cost %</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border/30">
                  <td className="py-2 px-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: selectedResult.etf.color }} />
                      <span className="font-bold text-foreground">{selectedResult.etf.ticker}</span>
                    </div>
                  </td>
                  <td className="py-2 px-2 text-right font-mono">${selectedResult.etf.currentPrice}</td>
                  <td className="py-2 px-2 text-right font-mono font-semibold">${selectedResult.strike}</td>
                  <td className="py-2 px-2 text-right font-mono text-primary">{selectedResult.beta}x</td>
                  <td className="py-2 px-2 text-right font-mono">{fmt(selectedResult.notional)}</td>
                  <td className="py-2 px-2 text-right font-bold">{selectedResult.contractsNeeded}</td>
                  <td className="py-2 px-2 text-right font-mono">${selectedResult.premiumPerShare}</td>
                  <td className="py-2 px-2 text-right font-mono text-destructive font-bold">{fmt(selectedResult.totalCost)}</td>
                  <td className="py-2 px-2 text-right font-mono">{selectedResult.costPct}%</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-muted-foreground mt-2">
            Contracts = Portfolio Value × Beta ÷ (ETF Price × 100). Beta adjusts for portfolio sensitivity to the index.
          </p>
        </CardContent>
      </Card>

      {/* Simulation Chart + Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Market Drop Simulation — {selectedResult.etf.ticker} Hedge</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={selectedResult.simulation} margin={{ top: 10, right: 20, bottom: 20, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
              <XAxis dataKey="marketMove" tick={{ fontSize: 10 }} tickFormatter={(v: number) => `${v > 0 ? '+' : ''}${v}%`} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v: number) => fmt(v)} labelFormatter={(l: number) => `Market ${l > 0 ? '+' : ''}${l}%`} contentStyle={{ fontSize: 11, borderRadius: 8 }} />
              <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" />
              <Area type="monotone" dataKey="unhedgedPL" stroke="#94a3b8" fill="#94a3b8" fillOpacity={0.1} name="Unhedged P&L" strokeWidth={2} />
              <Area type="monotone" dataKey="hedgedPL" stroke={selectedResult.etf.color} fill={selectedResult.etf.color} fillOpacity={0.15} name={`Hedged (${selectedResult.etf.ticker})`} strokeWidth={2.5} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
            </AreaChart>
          </ResponsiveContainer>

          <div className="overflow-x-auto mt-4">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="border-b-2 border-border">
                  <th className="text-left py-2 px-2 font-semibold text-foreground">Market</th>
                  <th className="text-right py-2 px-2 font-semibold text-foreground">Unhedged (USD)</th>
                  <th className="text-right py-2 px-2 font-semibold text-foreground">Unhedged (CHF)</th>
                  <th className="text-right py-2 px-2 font-semibold text-foreground">Put Payoff</th>
                  <th className="text-right py-2 px-2 font-semibold text-foreground">Hedged P&L (USD)</th>
                  <th className="text-right py-2 px-2 font-semibold text-foreground">Hedged P&L (CHF)</th>
                  <th className="text-right py-2 px-2 font-semibold text-foreground">Net Benefit</th>
                </tr>
              </thead>
              <tbody>
                {selectedResult.simulation.map(row => (
                  <tr key={row.marketMove} className={`border-b border-border/30 ${row.marketMove === 0 ? 'bg-muted/20 font-semibold' : ''}`}>
                    <td className={`py-1.5 px-2 font-mono ${row.marketMove < 0 ? 'text-destructive' : 'text-green-600 dark:text-green-400'}`}>
                      {row.marketMove > 0 ? '+' : ''}{row.marketMove}%
                    </td>
                    <td className={`py-1.5 px-2 text-right font-mono ${row.unhedgedPL < 0 ? 'text-destructive' : 'text-green-600 dark:text-green-400'}`}>
                      {fmt(row.unhedgedPL)}
                    </td>
                    <td className={`py-1.5 px-2 text-right font-mono ${row.unhedgedPL < 0 ? 'text-destructive' : 'text-green-600 dark:text-green-400'}`}>
                      {fmt(Math.round(row.unhedgedPL * USD_CHF), 'CHF')}
                    </td>
                    <td className="py-1.5 px-2 text-right font-mono text-primary">
                      {row.putPayoff > 0 ? fmt(row.putPayoff) : '—'}
                    </td>
                    <td className={`py-1.5 px-2 text-right font-mono font-semibold ${row.hedgedPL < 0 ? 'text-destructive' : 'text-green-600 dark:text-green-400'}`}>
                      {fmt(row.hedgedPL)}
                    </td>
                    <td className={`py-1.5 px-2 text-right font-mono ${row.hedgedPL < 0 ? 'text-destructive' : 'text-green-600 dark:text-green-400'}`}>
                      {fmt(Math.round(row.hedgedPL * USD_CHF), 'CHF')}
                    </td>
                    <td className={`py-1.5 px-2 text-right font-mono font-bold ${row.netBenefit > 0 ? 'text-green-600 dark:text-green-400' : row.netBenefit < 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
                      {fmt(row.netBenefit)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* All ETFs Comparison */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Index ETF Comparison — {tier.label}</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={comparisonData} margin={{ top: 10, right: 20, bottom: 5, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fontWeight: 600 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v: number, n: string) => [n.includes('CHF') ? fmt(v, 'CHF') : fmt(v), n]} contentStyle={{ fontSize: 11, borderRadius: 8 }} />
              <Bar dataKey="cost" name="Cost (USD)">
                {INDEX_ETFS.map((etf, i) => (
                  <Cell key={i} fill={etf.color} fillOpacity={i === selectedEtfIdx ? 1 : 0.4} />
                ))}
              </Bar>
              <Bar dataKey="costCHF" name="Cost (CHF)">
                {INDEX_ETFS.map((etf, i) => (
                  <Cell key={i} fill={etf.color} fillOpacity={i === selectedEtfIdx ? 0.7 : 0.25} />
                ))}
              </Bar>
              <Legend wrapperStyle={{ fontSize: 10 }} />
            </BarChart>
          </ResponsiveContainer>

          <div className="overflow-x-auto mt-4">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="border-b-2 border-border">
                  <th className="text-left py-2 px-2 font-semibold text-foreground">ETF</th>
                  <th className="text-right py-2 px-2 font-semibold text-foreground">Spot</th>
                  <th className="text-right py-2 px-2 font-semibold text-foreground">Strike</th>
                  <th className="text-right py-2 px-2 font-semibold text-foreground">Contracts</th>
                  <th className="text-right py-2 px-2 font-semibold text-foreground">Cost (USD)</th>
                  <th className="text-right py-2 px-2 font-semibold text-foreground">Cost (CHF)</th>
                  <th className="text-right py-2 px-2 font-semibold text-foreground">Cost %</th>
                  <th className="text-right py-2 px-2 font-semibold text-foreground">vs Individual</th>
                </tr>
              </thead>
              <tbody>
                {allResults.map((r, i) => {
                  const saving = individualCost - r.totalCost;
                  return (
                    <tr key={r.etf.ticker} className={`border-b border-border/30 ${i === selectedEtfIdx ? 'bg-primary/5 font-semibold' : ''}`}>
                      <td className="py-1.5 px-2">
                        <div className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: r.etf.color }} />
                          <span className="font-bold text-foreground">{r.etf.ticker}</span>
                          <span className="text-muted-foreground">{r.etf.name}</span>
                        </div>
                      </td>
                      <td className="py-1.5 px-2 text-right font-mono">${r.etf.currentPrice}</td>
                      <td className="py-1.5 px-2 text-right font-mono">${r.strike}</td>
                      <td className="py-1.5 px-2 text-right">{r.contractsNeeded}</td>
                      <td className="py-1.5 px-2 text-right font-mono text-destructive">{fmt(r.totalCost)}</td>
                      <td className="py-1.5 px-2 text-right font-mono text-destructive">{fmt(Math.round(r.totalCost * USD_CHF), 'CHF')}</td>
                      <td className="py-1.5 px-2 text-right font-mono">{r.costPct}%</td>
                      <td className={`py-1.5 px-2 text-right font-mono font-bold ${saving > 0 ? 'text-green-600 dark:text-green-400' : 'text-destructive'}`}>
                        {saving > 0 ? '-' : '+'}{fmt(Math.abs(saving))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-muted-foreground mt-3">
            <strong>vs Individual</strong> = cost saving compared to buying puts on each stock separately. Negative = index hedge is cheaper.
            Index hedges are typically cheaper but provide less precise coverage (basis risk).
          </p>
        </CardContent>
      </Card>
    </div>
  );
}


function CostTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-popover border border-border rounded-lg p-3 shadow-xl text-sm">
      <p className="font-semibold text-foreground mb-1">{label}% OTM Strike</p>
      {payload.map((p: any) => (
        <p key={p.dataKey} style={{ color: p.color }} className="text-xs">
          {p.name}: {typeof p.value === 'number' ? p.value.toLocaleString() : p.value}
        </p>
      ))}
    </div>
  );
}

// ─── Risk score color ──────────────────────────────────────────────

function riskToColor(score: number): string {
  if (score <= 2) return '#22c55e';
  if (score <= 4) return '#84cc16';
  if (score <= 6) return '#eab308';
  if (score <= 8) return '#f97316';
  return '#ef4444';
}

function riskLabel(score: number): string {
  if (score <= 3) return 'Low';
  if (score <= 6) return 'Medium';
  return 'High';
}

// ─── Sector detection ──────────────────────────────────────────────

const SECTOR_KEYWORDS: Record<string, string[]> = {
  Technology: ['tech', 'software', 'microsoft', 'apple', 'google', 'alphabet', 'meta', 'nvidia', 'amd', 'intel', 'ibm', 'oracle', 'sap', 'cisco', 'adobe', 'salesforce', 'paypal', 'broadcom', 'qualcomm', 'tsmc', 'samsung', 'asml', 'infosys', 'logitech', 'temenos', 'swissquote', 'u-blox'],
  Healthcare: ['pharma', 'health', 'medical', 'biotech', 'novartis', 'roche', 'pfizer', 'johnson', 'merck', 'abbvie', 'amgen', 'lilly', 'astrazeneca', 'novo nordisk', 'sanofi', 'bayer', 'gsk', 'lonza', 'sonova', 'straumann', 'galenic', 'siegfried', 'vifor', 'idorsia'],
  Finance: ['bank', 'financ', 'insur', 'credit', 'capital', 'invest', 'ubs', 'julius', 'zurich', 'swiss re', 'partners group', 'blackrock', 'jpmorgan', 'goldman', 'morgan stanley', 'visa', 'mastercard', 'cembra', 'vontobel', 'baloise', 'helvetia'],
  Industrial: ['industri', 'engineer', 'manufact', 'abb', 'siemens', 'schneider', 'schindler', 'geberit', 'georg fischer', 'oerlikon', 'sulzer', 'bucher', 'bobst', 'dormakaba', 'sgs', 'interroll', 'komax', 'bossard', 'belimo', 'burckhardt'],
  Consumer: ['nestle', 'consumer', 'retail', 'food', 'beverage', 'luxury', 'lvmh', 'hermes', 'richemont', 'swatch', 'lindt', 'coca-cola', 'pepsi', 'procter', 'unilever', 'nike', 'adidas', 'mcdonald', 'starbucks', 'walmart', 'amazon', 'barry callebaut', 'emmi', 'aryzta'],
  Energy: ['energy', 'oil', 'gas', 'petrol', 'solar', 'wind', 'exxon', 'chevron', 'shell', 'bp', 'total', 'enel', 'nextera', 'meyer burger'],
  Telecom: ['telecom', 'communic', 'swisscom', 'sunrise', 'vodafone', 'orange', 'telefonica', 'at&t', 'verizon', 't-mobile'],
  Materials: ['material', 'mining', 'metal', 'chemical', 'cement', 'holcim', 'sika', 'givaudan', 'clariant', 'ems-chemie', 'basf', 'linde'],
  RealEstate: ['real estate', 'property', 'reit', 'psp', 'allreal', 'mobimo', 'swiss prime'],
  Utilities: ['utilit', 'power', 'water', 'bkw', 'alpiq', 'axpo', 'romande energie'],
};

function detectSector(name: string): string {
  const lower = name.toLowerCase();
  for (const [sector, keywords] of Object.entries(SECTOR_KEYWORDS)) {
    if (keywords.some(k => lower.includes(k))) return sector;
  }
  return 'Other';
}

// ─── Correlation estimation ────────────────────────────────────────

function estimateCorrelation(a: HedgePosition, b: HedgePosition): number {
  if (a.ticker === b.ticker) return 1;
  
  let corr = 0.2; // base correlation (market beta)
  
  // Same currency adds correlation
  if (a.localCurrency === b.localCurrency) corr += 0.15;
  
  // Same sector adds significant correlation
  const sA = detectSector(a.stockName);
  const sB = detectSector(b.stockName);
  if (sA === sB && sA !== 'Other') corr += 0.35;
  else if (sA !== 'Other' && sB !== 'Other') corr += 0.05; // different known sectors
  
  // Similar gain profile adds correlation
  const gainDiff = Math.abs(a.gainPct - b.gainPct);
  if (gainDiff < 0.05) corr += 0.15;
  else if (gainDiff < 0.15) corr += 0.08;
  
  // Similar volatility profile
  const vA = estimateVolatility(a.gainPct);
  const vB = estimateVolatility(b.gainPct);
  if (Math.abs(vA - vB) < 0.05) corr += 0.1;
  
  return Math.min(corr, 0.95);
}

function buildCorrelationMatrix(positions: HedgePosition[]) {
  const n = positions.length;
  const matrix: number[][] = [];
  for (let i = 0; i < n; i++) {
    matrix[i] = [];
    for (let j = 0; j < n; j++) {
      matrix[i][j] = estimateCorrelation(positions[i], positions[j]);
    }
  }
  return matrix;
}

function corrToColor(corr: number): string {
  if (corr >= 0.8) return '#dc2626';
  if (corr >= 0.6) return '#f97316';
  if (corr >= 0.4) return '#eab308';
  if (corr >= 0.2) return '#84cc16';
  return '#22c55e';
}

function corrToBg(corr: number): string {
  const intensity = Math.round(corr * 255);
  // red channel increases with correlation
  const r = Math.round(34 + (220 - 34) * corr);
  const g = Math.round(197 - (197 - 38) * corr);
  const b = Math.round(94 - (94 - 38) * corr);
  return `rgb(${r}, ${g}, ${b})`;
}

// ─── Correlation Matrix Sub-component ──────────────────────────────

function CorrelationMatrix({ positions }: { positions: HedgePosition[] }) {
  const matrix = useMemo(() => buildCorrelationMatrix(positions), [positions]);
  const sectors = useMemo(() => positions.map(p => detectSector(p.stockName)), [positions]);
  
  // Diversification score: average off-diagonal correlation (lower = better)
  const avgCorr = useMemo(() => {
    if (positions.length < 2) return 0;
    let sum = 0;
    let count = 0;
    for (let i = 0; i < positions.length; i++) {
      for (let j = i + 1; j < positions.length; j++) {
        sum += matrix[i][j];
        count++;
      }
    }
    return count > 0 ? sum / count : 0;
  }, [matrix, positions]);

  const diversificationScore = Math.round((1 - avgCorr) * 100);

  // Highly correlated pairs
  const highPairs = useMemo(() => {
    const pairs: { a: string; b: string; corr: number }[] = [];
    for (let i = 0; i < positions.length; i++) {
      for (let j = i + 1; j < positions.length; j++) {
        if (matrix[i][j] >= 0.6) {
          pairs.push({ a: positions[i].ticker, b: positions[j].ticker, corr: matrix[i][j] });
        }
      }
    }
    return pairs.sort((a, b) => b.corr - a.corr).slice(0, 10);
  }, [matrix, positions]);

  // Sector concentration
  const sectorCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    sectors.forEach(s => { counts[s] = (counts[s] || 0) + 1; });
    return Object.entries(counts).sort(([, a], [, b]) => b - a);
  }, [sectors]);

  const shortTicker = (t: string) => t.length > 6 ? t.slice(0, 6) : t;

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-[10px] text-muted-foreground uppercase">Diversification Score</p>
            <p className={`text-2xl font-bold ${diversificationScore >= 70 ? 'text-green-600 dark:text-green-400' : diversificationScore >= 40 ? 'text-yellow-600 dark:text-yellow-400' : 'text-destructive'}`}>
              {diversificationScore}/100
            </p>
            <p className="text-[10px] text-muted-foreground">
              {diversificationScore >= 70 ? 'Well diversified' : diversificationScore >= 40 ? 'Moderate overlap' : 'High concentration risk'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-[10px] text-muted-foreground uppercase">Avg Correlation</p>
            <p className="text-2xl font-bold text-foreground">{(avgCorr * 100).toFixed(0)}%</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-[10px] text-muted-foreground uppercase">High Correlation Pairs</p>
            <p className="text-2xl font-bold text-destructive">{highPairs.length}</p>
            <p className="text-[10px] text-muted-foreground">≥60% correlated</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-[10px] text-muted-foreground uppercase">Sectors Covered</p>
            <p className="text-2xl font-bold text-primary">{sectorCounts.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Matrix Heatmap */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Grid3x3 className="h-4 w-4" />
            Correlation Matrix (Estimated)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <div className="inline-block min-w-max">
              <table className="border-collapse">
                <thead>
                  <tr>
                    <th className="w-16" />
                    {positions.map((p, j) => (
                      <th key={j} className="px-0.5 py-1 text-[8px] font-semibold text-foreground" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', maxHeight: 60 }}>
                        {shortTicker(p.ticker)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {positions.map((p, i) => (
                    <tr key={i}>
                      <td className="pr-1 py-0.5 text-[9px] font-semibold text-foreground text-right whitespace-nowrap">{shortTicker(p.ticker)}</td>
                      {positions.map((_, j) => {
                        const c = matrix[i][j];
                        const isDiag = i === j;
                        return (
                          <td
                            key={j}
                            className="p-0"
                            title={`${p.ticker} × ${positions[j].ticker}: ${(c * 100).toFixed(0)}%`}
                          >
                            <div
                              className="w-6 h-6 md:w-7 md:h-7 flex items-center justify-center text-[7px] font-mono font-bold border border-background/50"
                              style={{
                                backgroundColor: isDiag ? 'hsl(var(--muted))' : corrToBg(c),
                                color: c > 0.5 || isDiag ? '#fff' : '#1e293b',
                              }}
                            >
                              {isDiag ? '—' : (c * 100).toFixed(0)}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {/* Legend */}
          <div className="flex items-center gap-3 mt-3 justify-center flex-wrap">
            {[
              { label: 'Low (<20%)', color: '#22c55e' },
              { label: 'Moderate (20-40%)', color: '#84cc16' },
              { label: 'Medium (40-60%)', color: '#eab308' },
              { label: 'High (60-80%)', color: '#f97316' },
              { label: 'Very High (>80%)', color: '#dc2626' },
            ].map(l => (
              <div key={l.label} className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: l.color }} />
                <span className="text-[10px] text-muted-foreground">{l.label}</span>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-muted-foreground mt-2 text-center">
            Correlations estimated from sector, currency, gain profile, and volatility similarity. For precise values, use historical return data.
          </p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* High Correlation Pairs */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-destructive">⚠ Highly Correlated Pairs (≥60%)</CardTitle>
          </CardHeader>
          <CardContent>
            {highPairs.length === 0 ? (
              <p className="text-sm text-green-600 dark:text-green-400 py-4 text-center">✅ No highly correlated pairs found — good diversification!</p>
            ) : (
              <div className="space-y-1.5">
                {highPairs.map((hp, i) => (
                  <div key={i} className="flex items-center justify-between p-2 rounded-md bg-muted/20 border border-border/30">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-foreground">{hp.a}</span>
                      <span className="text-[10px] text-muted-foreground">×</span>
                      <span className="text-xs font-bold text-foreground">{hp.b}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${hp.corr * 100}%`, backgroundColor: corrToColor(hp.corr) }} />
                      </div>
                      <span className="text-xs font-mono font-bold" style={{ color: corrToColor(hp.corr) }}>
                        {(hp.corr * 100).toFixed(0)}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sector Distribution */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Sector Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {sectorCounts.map(([sector, count]) => {
                const pct = Math.round((count / positions.length) * 100);
                const isConcentrated = pct >= 40;
                return (
                  <div key={sector} className="space-y-0.5">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-semibold text-foreground">{sector}</span>
                      <span className={`font-mono ${isConcentrated ? 'text-destructive font-bold' : 'text-muted-foreground'}`}>
                        {count} positions ({pct}%)
                        {isConcentrated && ' ⚠'}
                      </span>
                    </div>
                    <div className="h-2 bg-muted/30 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${pct}%`,
                          backgroundColor: isConcentrated ? '#ef4444' : pct >= 25 ? '#f59e0b' : '#22c55e',
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            {sectorCounts.some(([, c]) => (c / positions.length) >= 0.4) && (
              <p className="text-[10px] text-destructive mt-3">
                ⚠ Sector concentration detected (≥40%). Consider diversifying to reduce correlated downside risk.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ─── Currency Exposure Sub-component ───────────────────────────────

const CURRENCY_COLORS: Record<string, string> = {
  USD: '#3b82f6', EUR: '#8b5cf6', CHF: '#ef4444', GBP: '#22c55e',
  SEK: '#f59e0b', NOK: '#06b6d4', DKK: '#ec4899', JPY: '#f97316',
  CAD: '#14b8a6', AUD: '#6366f1', HKD: '#a855f7', SGD: '#0ea5e9',
};

function CurrencyExposure({ positions, totalPortfolioValue }: { positions: HedgePosition[]; totalPortfolioValue: number }) {
  const data = useMemo(() => {
    const byCur: Record<string, { value: number; count: number; tickers: string[] }> = {};
    for (const p of positions) {
      const c = p.localCurrency || 'CHF';
      if (!byCur[c]) byCur[c] = { value: 0, count: 0, tickers: [] };
      byCur[c].value += p.totalValue;
      byCur[c].count++;
      byCur[c].tickers.push(p.ticker);
    }
    return Object.entries(byCur)
      .map(([currency, d]) => ({
        currency,
        value: Math.round(d.value),
        valueCHF: Math.round(d.value * (USD_CHF)), // approximate
        count: d.count,
        pct: Math.round((d.value / totalPortfolioValue) * 10000) / 100,
        tickers: d.tickers,
        fill: CURRENCY_COLORS[currency] || '#94a3b8',
      }))
      .sort((a, b) => b.value - a.value);
  }, [positions, totalPortfolioValue]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Pie Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Coins className="h-4 w-4" />
              Currency Allocation
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={data}
                  dataKey="value"
                  nameKey="currency"
                  cx="50%"
                  cy="50%"
                  outerRadius={110}
                  innerRadius={55}
                  paddingAngle={2}
                  label={({ currency, pct }) => `${currency} ${pct}%`}
                  labelLine={{ strokeWidth: 1 }}
                >
                  {data.map((d, i) => (
                    <Cell key={i} fill={d.fill} stroke={d.fill} strokeWidth={1} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number, name: string) => [fmt(value), name]}
                  contentStyle={{ fontSize: 11, borderRadius: 8 }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Bar Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Exposure by Currency (USD)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data} layout="vertical" margin={{ top: 5, right: 20, bottom: 5, left: 40 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="currency" tick={{ fontSize: 11, fontWeight: 600 }} width={40} />
                <Tooltip formatter={(v: number) => fmt(v)} contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                <Bar dataKey="value" name="Value (USD)" radius={[0, 4, 4, 0]}>
                  {data.map((d, i) => (
                    <Cell key={i} fill={d.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Detail Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Currency Breakdown — Positions Detail</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="border-b-2 border-border">
                  <th className="text-left py-2 px-3 font-semibold text-foreground">Currency</th>
                  <th className="text-right py-2 px-3 font-semibold text-foreground">Positions</th>
                  <th className="text-right py-2 px-3 font-semibold text-foreground">Value (USD)</th>
                  <th className="text-right py-2 px-3 font-semibold text-foreground">Value (CHF)</th>
                  <th className="text-right py-2 px-3 font-semibold text-foreground">Weight</th>
                  <th className="text-left py-2 px-3 font-semibold text-foreground">Tickers</th>
                </tr>
              </thead>
              <tbody>
                {data.map(d => (
                  <tr key={d.currency} className="border-b border-border/30 hover:bg-muted/20">
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: d.fill }} />
                        <span className="font-bold text-foreground">{d.currency}</span>
                      </div>
                    </td>
                    <td className="py-2 px-3 text-right">{d.count}</td>
                    <td className="py-2 px-3 text-right font-mono">{fmt(d.value)}</td>
                    <td className="py-2 px-3 text-right font-mono">{fmt(d.valueCHF, 'CHF')}</td>
                    <td className="py-2 px-3 text-right font-mono font-semibold">{d.pct}%</td>
                    <td className="py-2 px-3 text-muted-foreground text-[10px] max-w-[250px] truncate">
                      {d.tickers.join(', ')}
                    </td>
                  </tr>
                ))}
                <tr className="border-t-2 border-border bg-muted/20 font-bold">
                  <td className="py-2 px-3 text-foreground">Total</td>
                  <td className="py-2 px-3 text-right">{positions.length}</td>
                  <td className="py-2 px-3 text-right font-mono">{fmt(totalPortfolioValue)}</td>
                  <td className="py-2 px-3 text-right font-mono">{fmt(toChf(totalPortfolioValue), 'CHF')}</td>
                  <td className="py-2 px-3 text-right font-mono">100%</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Risk Heatmap Sub-component ────────────────────────────────────

function RiskHeatmap({ analyses, positions, totalPortfolioValue, tier }: {
  analyses: HedgeAnalysis[];
  positions: HedgePosition[];
  totalPortfolioValue: number;
  tier: ProtectionTier;
}) {
  const heatData = useMemo(() => {
    return analyses
      .map(a => {
        const weight = a.portfolioWeight;
        const vol = estimateVolatility(a.position.gainPct);
        const hedgeCost = computeTierCost([a.position], tier).totalCostUSD;
        const hedgeCostPct = (hedgeCost / a.position.totalValue) * 100;
        // Composite risk = weighted combo of risk score, weight, volatility
        const compositeRisk = Math.min(10, a.riskScore * 0.5 + weight * 0.3 + vol * 10 * 0.2);
        
        return {
          ticker: a.position.ticker,
          name: a.position.stockName,
          riskScore: a.riskScore,
          weight,
          value: a.position.totalValue,
          gainPct: Math.round(a.position.gainPct * 100 * 10) / 10,
          volatility: Math.round(vol * 100),
          hedgeCost,
          hedgeCostPct: Math.round(hedgeCostPct * 100) / 100,
          compositeRisk: Math.round(compositeRisk * 10) / 10,
          currency: a.position.localCurrency,
        };
      })
      .sort((a, b) => b.compositeRisk - a.compositeRisk);
  }, [analyses, tier]);

  const scatterData = useMemo(() => heatData.map(d => ({
    x: d.weight,
    y: d.volatility,
    z: d.value,
    ticker: d.ticker,
    riskScore: d.riskScore,
    compositeRisk: d.compositeRisk,
  })), [heatData]);

  return (
    <div className="space-y-4">
      {/* Bubble Chart: Weight vs Volatility, size = value */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Flame className="h-4 w-4 text-orange-500" />
            Risk Bubble Map — Portfolio Weight vs. Volatility
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={320}>
            <ScatterChart margin={{ top: 10, right: 20, bottom: 25, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
              <XAxis type="number" dataKey="x" name="Weight" unit="%" tick={{ fontSize: 10 }} label={{ value: 'Portfolio Weight (%)', position: 'insideBottom', offset: -15, style: { fontSize: 11 } }} />
              <YAxis type="number" dataKey="y" name="Volatility" unit="%" tick={{ fontSize: 10 }} label={{ value: 'Est. Volatility (%)', angle: -90, position: 'insideLeft', style: { fontSize: 10 } }} />
              <ZAxis type="number" dataKey="z" range={[100, 2000]} name="Value" />
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0].payload;
                  return (
                    <div className="bg-popover border border-border rounded-lg p-3 shadow-xl text-xs">
                      <p className="font-bold text-foreground text-sm">{d.ticker}</p>
                      <p className="text-muted-foreground">Weight: {d.x.toFixed(1)}%</p>
                      <p className="text-muted-foreground">Volatility: {d.y}%</p>
                      <p className="text-muted-foreground">Value: {fmt(d.z)}</p>
                      <p style={{ color: riskToColor(d.riskScore) }}>Risk: {d.riskScore}/10 ({riskLabel(d.riskScore)})</p>
                    </div>
                  );
                }}
              />
              <Scatter data={scatterData}>
                {scatterData.map((entry, i) => (
                  <Cell key={i} fill={riskToColor(entry.riskScore)} fillOpacity={0.75} stroke={riskToColor(entry.riskScore)} strokeWidth={1.5} />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
          <p className="text-xs text-muted-foreground mt-1">Bubble size = position value. Color = risk score (green→red). Top-right = highest priority for hedging.</p>
        </CardContent>
      </Card>

      {/* Visual Heatmap Grid */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Position Risk Heatmap</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
            {heatData.map(d => (
              <div
                key={d.ticker}
                className="relative rounded-lg p-3 border transition-all hover:scale-105 cursor-default"
                style={{
                  backgroundColor: `${riskToColor(d.riskScore)}15`,
                  borderColor: `${riskToColor(d.riskScore)}40`,
                }}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold text-foreground">{d.ticker}</span>
                  <span
                    className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                    style={{ backgroundColor: `${riskToColor(d.riskScore)}25`, color: riskToColor(d.riskScore) }}
                  >
                    {d.riskScore}/10
                  </span>
                </div>
                <p className="text-[10px] text-muted-foreground truncate">{d.name}</p>
                <div className="mt-1.5 space-y-0.5">
                  <div className="flex justify-between text-[10px]">
                    <span className="text-muted-foreground">Weight</span>
                    <span className="font-mono text-foreground">{d.weight.toFixed(1)}%</span>
                  </div>
                  <div className="flex justify-between text-[10px]">
                    <span className="text-muted-foreground">Gain</span>
                    <span className={`font-mono ${d.gainPct >= 0 ? 'text-green-600 dark:text-green-400' : 'text-destructive'}`}>{d.gainPct > 0 ? '+' : ''}{d.gainPct}%</span>
                  </div>
                  <div className="flex justify-between text-[10px]">
                    <span className="text-muted-foreground">IV Est.</span>
                    <span className="font-mono text-foreground">{d.volatility}%</span>
                  </div>
                  <div className="flex justify-between text-[10px]">
                    <span className="text-muted-foreground">Hedge $</span>
                    <span className="font-mono text-destructive">{fmt(d.hedgeCost)}</span>
                  </div>
                </div>
                {/* Risk bar */}
                <div className="mt-2 h-1.5 bg-muted/30 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${d.riskScore * 10}%`, backgroundColor: riskToColor(d.riskScore) }}
                  />
                </div>
              </div>
            ))}
          </div>
          {/* Legend */}
          <div className="flex items-center gap-4 mt-4 justify-center">
            {[{ s: 2, l: 'Low' }, { s: 5, l: 'Medium' }, { s: 8, l: 'High' }, { s: 10, l: 'Critical' }].map(({ s, l }) => (
              <div key={l} className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: riskToColor(s) }} />
                <span className="text-[10px] text-muted-foreground">{l}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Detailed risk table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Risk Ranking — Hedge Priority</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="border-b-2 border-border">
                  <th className="text-left py-2 px-2 font-semibold text-foreground">#</th>
                  <th className="text-left py-2 px-2 font-semibold text-foreground">Ticker</th>
                  <th className="text-left py-2 px-2 font-semibold text-foreground">Name</th>
                  <th className="text-right py-2 px-2 font-semibold text-foreground">Value</th>
                  <th className="text-right py-2 px-2 font-semibold text-foreground">Weight</th>
                  <th className="text-right py-2 px-2 font-semibold text-foreground">Gain</th>
                  <th className="text-right py-2 px-2 font-semibold text-foreground">Vol</th>
                  <th className="text-right py-2 px-2 font-semibold text-foreground">Risk</th>
                  <th className="text-right py-2 px-2 font-semibold text-foreground">Composite</th>
                  <th className="text-right py-2 px-2 font-semibold text-foreground">Hedge Cost</th>
                  <th className="text-right py-2 px-2 font-semibold text-foreground">Cost %</th>
                  <th className="text-center py-2 px-2 font-semibold text-foreground">Priority</th>
                </tr>
              </thead>
              <tbody>
                {heatData.map((d, i) => (
                  <tr key={d.ticker} className={`border-b border-border/30 ${i < 3 ? 'bg-red-500/5' : ''}`}>
                    <td className="py-1.5 px-2 text-muted-foreground">{i + 1}</td>
                    <td className="py-1.5 px-2 font-bold text-foreground">{d.ticker}</td>
                    <td className="py-1.5 px-2 text-muted-foreground max-w-[100px] truncate">{d.name}</td>
                    <td className="py-1.5 px-2 text-right font-mono">{fmt(d.value)}</td>
                    <td className="py-1.5 px-2 text-right font-mono">{d.weight.toFixed(1)}%</td>
                    <td className={`py-1.5 px-2 text-right font-mono ${d.gainPct >= 0 ? 'text-green-600 dark:text-green-400' : 'text-destructive'}`}>
                      {d.gainPct > 0 ? '+' : ''}{d.gainPct}%
                    </td>
                    <td className="py-1.5 px-2 text-right font-mono">{d.volatility}%</td>
                    <td className="py-1.5 px-2 text-right">
                      <span className="font-bold" style={{ color: riskToColor(d.riskScore) }}>{d.riskScore}/10</span>
                    </td>
                    <td className="py-1.5 px-2 text-right">
                      <span className="font-bold" style={{ color: riskToColor(d.compositeRisk) }}>{d.compositeRisk}</span>
                    </td>
                    <td className="py-1.5 px-2 text-right font-mono text-destructive">{fmt(d.hedgeCost)}</td>
                    <td className="py-1.5 px-2 text-right font-mono">{d.hedgeCostPct}%</td>
                    <td className="py-1.5 px-2 text-center">
                      <Badge
                        variant="outline"
                        className="text-[10px]"
                        style={{ borderColor: riskToColor(d.compositeRisk), color: riskToColor(d.compositeRisk) }}
                      >
                        {i < 3 ? '🔴 Hedge Now' : i < 6 ? '🟡 Consider' : '🟢 Optional'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── PDF Export ─────────────────────────────────────────────────────

function generateHedgePDF(
  positions: HedgePosition[],
  totalPortfolioValue: number,
  tier: ProtectionTier,
  tierCosts: (ProtectionTier & { totalCostUSD: number })[],
  simData: ReturnType<typeof generateSimulationData>,
  optionRecs: ReturnType<typeof generateOptionRecommendations>,
  analyses: HedgeAnalysis[],
  selectedCost: { totalCostUSD: number },
  returnDoc = false,
): jsPDF {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 14;
  let y = margin;

  const addHeader = () => {
    doc.setFillColor(15, 23, 42);
    doc.rect(0, 0, pageW, 22, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Portfolio Hedge Protection Report', margin, 15);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated: ${new Date().toLocaleString()} | Tier: ${tier.label}`, pageW - margin, 15, { align: 'right' });
    doc.setTextColor(0, 0, 0);
    y = 30;
  };

  const addFooter = (pageNum: number) => {
    doc.setFontSize(7);
    doc.setTextColor(150);
    doc.text(`Page ${pageNum} — Hedge Protection Report — Confidential`, pageW / 2, pageH - 6, { align: 'center' });
    doc.setTextColor(0);
  };

  const checkPage = (needed: number, pageNum: { v: number }) => {
    if (y + needed > pageH - 15) {
      addFooter(pageNum.v);
      doc.addPage();
      pageNum.v++;
      addHeader();
    }
  };

  const pg = { v: 1 };

  // ── Page 1: Summary ──
  addHeader();

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Portfolio Summary', margin, y);
  y += 7;

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [['Metric', 'USD', 'CHF']],
    body: [
      ['Portfolio Value', fmt(totalPortfolioValue), fmt(toChf(totalPortfolioValue), 'CHF')],
      ['Hedge Cost (' + tier.label.split('(')[0].trim() + ')', fmt(selectedCost.totalCostUSD), fmt(toChf(selectedCost.totalCostUSD), 'CHF')],
      ['Cost as % of Portfolio', ((selectedCost.totalCostUSD / totalPortfolioValue) * 100).toFixed(2) + '%', ''],
      ['Positions', String(positions.length), ''],
    ],
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [15, 23, 42], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    theme: 'grid',
  });
  y = (doc as any).lastAutoTable.finalY + 10;

  // ── Tier Comparison ──
  checkPage(50, pg);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Protection Tier Comparison', margin, y);
  y += 7;

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [['Tier', 'OTM %', 'Expiry', 'Cost (USD)', 'Cost (CHF)', '% Portfolio']],
    body: tierCosts.map(t => [
      t.label.split('(')[0].trim(),
      `${t.otmPct * 100}%`,
      `${t.daysToExpiry}d`,
      fmt(t.totalCostUSD),
      fmt(toChf(t.totalCostUSD), 'CHF'),
      ((t.totalCostUSD / totalPortfolioValue) * 100).toFixed(2) + '%',
    ]),
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [15, 23, 42], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    theme: 'grid',
  });
  y = (doc as any).lastAutoTable.finalY + 10;

  // ── Loss/Profit Simulation ──
  checkPage(60, pg);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(`Market Move Simulation — ${tier.label}`, margin, y);
  y += 7;

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [['Market Move', 'Unhedged (USD)', 'Unhedged (CHF)', 'Put Payoff', 'Hedge Cost', 'Hedged P&L (USD)', 'Hedged P&L (CHF)', 'Net Benefit']],
    body: simData.map(r => [
      `${r.marketMove > 0 ? '+' : ''}${r.marketMove}%`,
      fmt(r.unhedgedPL_USD),
      fmt(r.unhedgedPL_CHF, 'CHF'),
      r.putPayoff_USD > 0 ? fmt(r.putPayoff_USD) : '—',
      fmt(r.hedgeCost_USD),
      fmt(r.hedgedPL_USD),
      fmt(r.hedgedPL_CHF, 'CHF'),
      fmt(r.netBenefit_USD),
    ]),
    styles: { fontSize: 7, cellPadding: 1.5 },
    headStyles: { fillColor: [15, 23, 42], textColor: 255, fontStyle: 'bold', fontSize: 7 },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    theme: 'grid',
    didParseCell: (data: any) => {
      if (data.section === 'body') {
        const col = data.column.index;
        const val = simData[data.row.index];
        if (col === 0 && val.marketMove < 0) data.cell.styles.textColor = [220, 38, 38];
        if (col === 0 && val.marketMove > 0) data.cell.styles.textColor = [22, 163, 74];
        if ((col === 5 || col === 6) && val.hedgedPL_USD < 0) data.cell.styles.textColor = [220, 38, 38];
        if ((col === 5 || col === 6) && val.hedgedPL_USD >= 0) data.cell.styles.textColor = [22, 163, 74];
        if (col === 7 && val.netBenefit_USD > 0) data.cell.styles.textColor = [22, 163, 74];
        if (col === 7 && val.netBenefit_USD < 0) data.cell.styles.textColor = [220, 38, 38];
      }
    },
  });
  y = (doc as any).lastAutoTable.finalY + 10;

  // ── Options to Buy ──
  addFooter(pg.v);
  doc.addPage();
  pg.v++;
  addHeader();

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(`Recommended Put Options — ${tier.label}`, margin, y);
  y += 7;

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [['Ticker', 'Name', 'Spot', 'Qty', 'Type', 'Strike', 'Expiry', 'Prem/sh', 'Contracts', 'Total Cost', 'Deductible']],
    body: [
      ...optionRecs.map(r => [
        r.ticker,
        r.stockName.length > 20 ? r.stockName.slice(0, 20) + '…' : r.stockName,
        fmt(r.currentPrice),
        String(r.quantity),
        'PUT',
        fmt(r.strike),
        r.expiry,
        fmt(r.premiumPerShare),
        String(r.contracts),
        fmt(r.totalCost),
        fmt(r.deductible),
      ]),
      ['', '', '', '', '', '', '', '', 'TOTAL:', fmt(selectedCost.totalCostUSD), fmt(toChf(selectedCost.totalCostUSD), 'CHF')],
    ],
    styles: { fontSize: 7, cellPadding: 1.5 },
    headStyles: { fillColor: [15, 23, 42], textColor: 255, fontStyle: 'bold', fontSize: 7 },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    theme: 'grid',
    didParseCell: (data: any) => {
      if (data.section === 'body' && data.row.index === optionRecs.length) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [230, 235, 245];
      }
    },
  });
  y = (doc as any).lastAutoTable.finalY + 10;

  // ── Risk Ranking ──
  checkPage(60, pg);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Risk Ranking — Hedge Priority', margin, y);
  y += 7;

  const riskData = analyses
    .map(a => {
      const weight = a.portfolioWeight;
      const vol = estimateVolatility(a.position.gainPct);
      const hedgeCost = computeTierCost([a.position], tier).totalCostUSD;
      const compositeRisk = Math.min(10, a.riskScore * 0.5 + weight * 0.3 + vol * 10 * 0.2);
      return {
        ticker: a.position.ticker,
        name: a.position.stockName,
        value: a.position.totalValue,
        weight,
        gainPct: Math.round(a.position.gainPct * 100 * 10) / 10,
        volatility: Math.round(vol * 100),
        riskScore: a.riskScore,
        compositeRisk: Math.round(compositeRisk * 10) / 10,
        hedgeCost,
        priority: '',
      };
    })
    .sort((a, b) => b.compositeRisk - a.compositeRisk)
    .map((d, i) => ({ ...d, priority: i < 3 ? 'HEDGE NOW' : i < 6 ? 'CONSIDER' : 'OPTIONAL' }));

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [['#', 'Ticker', 'Name', 'Value', 'Weight', 'Gain', 'Vol', 'Risk', 'Composite', 'Hedge Cost', 'Priority']],
    body: riskData.map((d, i) => [
      String(i + 1),
      d.ticker,
      d.name.length > 18 ? d.name.slice(0, 18) + '…' : d.name,
      fmt(d.value),
      d.weight.toFixed(1) + '%',
      `${d.gainPct > 0 ? '+' : ''}${d.gainPct}%`,
      d.volatility + '%',
      `${d.riskScore}/10`,
      String(d.compositeRisk),
      fmt(d.hedgeCost),
      d.priority,
    ]),
    styles: { fontSize: 7, cellPadding: 1.5 },
    headStyles: { fillColor: [15, 23, 42], textColor: 255, fontStyle: 'bold', fontSize: 7 },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    theme: 'grid',
    didParseCell: (data: any) => {
      if (data.section === 'body') {
        const col = data.column.index;
        if (col === 10) {
          const p = riskData[data.row.index]?.priority;
          if (p === 'HEDGE NOW') { data.cell.styles.textColor = [220, 38, 38]; data.cell.styles.fontStyle = 'bold'; }
          else if (p === 'CONSIDER') { data.cell.styles.textColor = [202, 138, 4]; }
          else { data.cell.styles.textColor = [22, 163, 74]; }
        }
      }
    },
  });

  addFooter(pg.v);

  // ── Strategy Comparison & Best Recommendation ──
  doc.addPage();
  pg.v++;
  addHeader();

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('Strategy Comparison — Best Recommendation per Stock', margin, y);
  y += 7;

  // P&L computation helper (inline for PDF context)
  const computeStratPnL = (a: HedgeAnalysis, strategy: 'put' | 'collar' | 'covered', pricePct: number, putIdx: number) => {
    const pos = a.position;
    const spot = pos.currentPrice;
    const qty = pos.quantity;
    const newPrice = spot * (1 + pricePct / 100);
    const stockPnL = (newPrice - spot) * qty;

    if (strategy === 'put') {
      const put = a.protectivePuts[putIdx] || a.protectivePuts[0];
      const contracts = put.contractsNeeded;
      const putPayoff = Math.max(put.strikePrice - newPrice, 0) * contracts * 100;
      return stockPnL + putPayoff - put.totalCost;
    }
    if (strategy === 'collar') {
      const c = a.collar;
      const contracts = c.contractsNeeded;
      const putPayoff = Math.max(c.putStrike - newPrice, 0) * contracts * 100;
      const callPayoff = -Math.max(newPrice - c.callStrike, 0) * contracts * 100;
      const premCost = c.netPremium > 0 ? c.netPremium : 0;
      const premCredit = c.netPremium < 0 ? -c.netPremium : 0;
      return stockPnL + putPayoff + callPayoff - premCost + premCredit;
    }
    // covered call
    const cc = a.coveredCall;
    const contracts = cc.contractsNeeded;
    const callPayoff = -Math.max(newPrice - cc.strikePrice, 0) * contracts * 100;
    return stockPnL + cc.incomeGenerated + callPayoff;
  };

  type StrategyDef = { key: string; label: string; strategy: 'put' | 'collar' | 'covered'; putIndex: number };

  const comparisonRows: string[][] = [];

  analyses.forEach(a => {
    const hasCoveredCall = a.coveredCall.contractsNeeded > 0;
    const strats: StrategyDef[] = [
      ...a.protectivePuts.map((p, i) => ({ key: `put-${i}`, label: `Put ${p.protectionLevel}`, strategy: 'put' as const, putIndex: i })),
      { key: 'collar', label: 'Collar', strategy: 'collar' as const, putIndex: 0 },
      ...(hasCoveredCall ? [{ key: 'covered', label: 'Covered Call', strategy: 'covered' as const, putIndex: 0 }] : []),
    ];

    // Weighted scenario analysis
    const scenarioPcts = [-50, -40, -30, -20, -10, -5, 0, 5, 10, 20, 30, 40, 50];
    const getWeight = (pct: number) => {
      if (pct <= -30) return 1; // crash
      if (pct <= -10) return 2; // bear
      if (pct <= 10) return 3;  // flat (most likely)
      if (pct <= 30) return 2;  // bull
      return 1; // rally
    };

    let bestLabel = '';
    let bestAvg = -Infinity;
    const stratResults: { label: string; avg: number; scenariosWon: number }[] = [];

    strats.forEach(s => {
      let total = 0, totalW = 0;
      scenarioPcts.forEach(pct => {
        const w = getWeight(pct);
        total += computeStratPnL(a, s.strategy, pct, s.putIndex) * w;
        totalW += w;
      });
      const avg = total / totalW;
      stratResults.push({ label: s.label, avg, scenariosWon: 0 });
      if (avg > bestAvg) { bestAvg = avg; bestLabel = s.label; }
    });

    // Count scenario wins for the best two
    const sorted = [...stratResults].sort((a, b) => b.avg - a.avg);
    const topTwo = sorted.slice(0, 2);

    // Scenario wins between top two
    let wins1 = 0, wins2 = 0;
    const s1 = strats.find(s => s.label === topTwo[0].label)!;
    const s2 = topTwo.length > 1 ? strats.find(s => s.label === topTwo[1].label) : null;
    scenarioPcts.forEach(pct => {
      const a1 = computeStratPnL(a, s1.strategy, pct, s1.putIndex);
      const a2 = s2 ? computeStratPnL(a, s2.strategy, pct, s2.putIndex) : -Infinity;
      if (a1 > a2) wins1++;
      else if (a2 > a1) wins2++;
    });

    const margin2 = bestAvg - (topTwo.length > 1 ? topTwo[1].avg : 0);
    const confidence = margin2 > 1000 ? 'High' : margin2 > 200 ? 'Medium' : 'Low';

    comparisonRows.push([
      a.position.ticker,
      a.position.stockName.length > 16 ? a.position.stockName.slice(0, 16) + '…' : a.position.stockName,
      fmt(a.position.totalValue),
      bestLabel,
      confidence,
      fmt(bestAvg),
      topTwo.length > 1 ? topTwo[1].label : '—',
      topTwo.length > 1 ? fmt(topTwo[1].avg) : '—',
      `${wins1} vs ${wins2}`,
      fmt(margin2),
    ]);
  });

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [['Ticker', 'Name', 'Value', '🏆 Best Strategy', 'Confidence', 'Wtd Avg P&L', '2nd Best', 'Wtd Avg P&L', 'Scenarios', 'Margin']],
    body: comparisonRows,
    styles: { fontSize: 6.5, cellPadding: 1.5 },
    headStyles: { fillColor: [15, 23, 42], textColor: 255, fontStyle: 'bold', fontSize: 6.5 },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    theme: 'grid',
    didParseCell: (data: any) => {
      if (data.section === 'body') {
        const col = data.column.index;
        // Best strategy column - bold green
        if (col === 3) { data.cell.styles.fontStyle = 'bold'; data.cell.styles.textColor = [22, 163, 74]; }
        // Confidence column
        if (col === 4) {
          const conf = comparisonRows[data.row.index]?.[4];
          if (conf === 'High') data.cell.styles.textColor = [22, 163, 74];
          else if (conf === 'Medium') data.cell.styles.textColor = [202, 138, 4];
          else data.cell.styles.textColor = [150, 150, 150];
        }
        // Weighted avg P&L columns
        if (col === 5 || col === 7) {
          const val = parseFloat(String(data.cell.text).replace(/[,$]/g, ''));
          if (!isNaN(val)) {
            data.cell.styles.textColor = val >= 0 ? [22, 163, 74] : [220, 38, 38];
          }
        }
      }
    },
  });
  y = (doc as any).lastAutoTable.finalY + 8;

  // Note
  checkPage(15, pg);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(120);
  doc.text('Weighted average P&L: Flat scenarios (±10%) weighted 3×, Bear/Bull (±30%) weighted 2×, Crash/Rally weighted 1×.', margin, y);
  y += 4;
  doc.text('Confidence: High = >$1,000 margin, Medium = >$200, Low = <$200 between 1st and 2nd best strategy.', margin, y);
  doc.setTextColor(0);
  y += 10;

  addFooter(pg.v);

  // ── Greeks Analysis ──────────────────────────────────────────────

  // Helper: estimate premium (same as hedgeAnalysis)
  const estPrem = (spot: number, strike: number, ivVal: number, dte: number, type: 'put' | 'call') => {
    return estimateOptionPremium(spot, strike, ivVal, Math.max(dte, 0.5), type);
  };

  // ── Page: Vega Sensitivity ──
  doc.addPage();
  pg.v++;
  addHeader();

  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text('Greeks Analysis — Vega Sensitivity', margin, y);
  y += 3;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(100);
  doc.text('Premium change per +1% IV move across moneyness levels. Higher vega = more sensitivity to volatility changes.', margin, y);
  doc.setTextColor(0);
  y += 6;

  const moneyLevels = [
    { label: '5% ITM', factor: 1.05 },
    { label: 'ATM', factor: 1.0 },
    { label: '5% OTM', factor: 0.95 },
    { label: '10% OTM', factor: 0.90 },
    { label: '15% OTM', factor: 0.85 },
  ];

  // Per-position vega table
  const vegaTableHead = [['Ticker', 'Spot', 'IV', 'DTE', ...moneyLevels.map(m => `Vega ${m.label}`)]];
  const vegaTableBody: (string | number)[][] = [];

  for (const a of analyses) {
    const pos = a.position;
    const spot = pos.currentPrice;
    const iv = estimateVolatility(pos.gainPct);
    const dte = 90;
    const row: (string | number)[] = [pos.ticker, `$${spot.toFixed(2)}`, `${(iv * 100).toFixed(0)}%`, `${dte}d`];
    for (const ml of moneyLevels) {
      const strike = spot * ml.factor;
      const premBase = estPrem(spot, strike, iv, dte, 'put');
      const premUp = estPrem(spot, strike, iv + 0.01, dte, 'put');
      row.push(`$${(premUp - premBase).toFixed(3)}`);
    }
    vegaTableBody.push(row);
  }

  autoTable(doc, {
    startY: y,
    head: vegaTableHead,
    body: vegaTableBody,
    theme: 'grid',
    styles: { fontSize: 7, cellPadding: 1.5 },
    headStyles: { fillColor: [15, 23, 42], textColor: 255, fontStyle: 'bold', fontSize: 7 },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    margin: { left: margin, right: margin },
  });
  y = (doc as any).lastAutoTable.finalY + 6;

  doc.setFontSize(7);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(100);
  doc.text('ATM options have the highest vega — most sensitive to IV changes. Deep OTM options are cheaper but less responsive.', margin, y);
  doc.setTextColor(0);
  y += 8;

  // ── Gamma Exposure Table ──
  checkPage(50, pg);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0);
  doc.text('Greeks Analysis — Gamma Exposure', margin, y);
  y += 3;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(100);
  doc.text('Gamma measures delta acceleration. Higher gamma near expiry = explosive hedging needs from small price moves.', margin, y);
  doc.setTextColor(0);
  y += 6;

  const dteSteps = [90, 60, 45, 30, 14, 7, 1];
  const gammaTableHead = [['Ticker', 'Moneyness', ...dteSteps.map(d => `${d}d`)]];
  const gammaTableBody: (string | number)[][] = [];

  const computeGamma = (spot: number, strike: number, iv: number, dte: number) => {
    const h = 0.5;
    const pUp = estPrem(spot + h, strike, iv, dte, 'put');
    const pMid = estPrem(spot, strike, iv, dte, 'put');
    const pDown = estPrem(spot - h, strike, iv, dte, 'put');
    return Math.abs((pUp - 2 * pMid + pDown) / (h * h));
  };

  for (const a of analyses.slice(0, 8)) { // limit for space
    const pos = a.position;
    const spot = pos.currentPrice;
    const iv = estimateVolatility(pos.gainPct);
    for (const ml of [moneyLevels[0], moneyLevels[1], moneyLevels[2]]) { // ITM, ATM, OTM
      const strike = spot * ml.factor;
      const row: (string | number)[] = [pos.ticker, ml.label];
      for (const dte of dteSteps) {
        const gamma = computeGamma(spot, strike, iv, dte);
        row.push(gamma.toFixed(4));
      }
      gammaTableBody.push(row);
    }
  }

  autoTable(doc, {
    startY: y,
    head: gammaTableHead,
    body: gammaTableBody,
    theme: 'grid',
    styles: { fontSize: 6.5, cellPadding: 1.2 },
    headStyles: { fillColor: [15, 23, 42], textColor: 255, fontStyle: 'bold', fontSize: 6.5 },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    margin: { left: margin, right: margin },
    didParseCell: (data: any) => {
      if (data.section === 'body' && data.column.index >= 2) {
        const val = parseFloat(data.cell.raw as string);
        if (val > 0.05) {
          data.cell.styles.fillColor = [254, 226, 226]; // red tint for high gamma
          data.cell.styles.fontStyle = 'bold';
        } else if (val > 0.02) {
          data.cell.styles.fillColor = [254, 249, 195]; // yellow tint
        }
      }
    },
  });
  y = (doc as any).lastAutoTable.finalY + 6;

  doc.setFontSize(7);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(100);
  doc.text('Red cells = high gamma (>0.05). Gamma spikes in the last 7-14 days — roll hedges 2-3 weeks before expiry.', margin, y);
  doc.setTextColor(0);
  y += 8;

  // ── Delta Surface Heatmap ──
  doc.addPage();
  pg.v++;
  addHeader();

  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text('Greeks Analysis — Delta Surface', margin, y);
  y += 3;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(100);
  doc.text('Put delta across stock price (% of spot) and days to expiry. Delta near -1.0 = deep ITM, near 0 = far OTM.', margin, y);
  doc.setTextColor(0);
  y += 6;

  // Show delta surface for the top positions
  const topPositions = analyses.slice(0, 4);
  const pricePcts = [70, 75, 80, 85, 90, 95, 100, 105, 110, 115, 120, 125, 130];
  const surfaceDteSteps = [1, 7, 14, 30, 45, 60, 90, 120];

  for (const a of topPositions) {
    const pos = a.position;
    const spot = pos.currentPrice;
    const iv = estimateVolatility(pos.gainPct);
    const strike = spot * 0.95; // 5% OTM put

    checkPage(35, pg);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0);
    doc.text(`${pos.ticker} — Strike $${strike.toFixed(0)} (95% of $${spot.toFixed(0)})  |  IV: ${(iv * 100).toFixed(0)}%`, margin, y);
    y += 4;

    const deltaHead = [['Price / DTE', ...surfaceDteSteps.map(d => `${d}d`)]];
    const deltaBody: (string | number)[][] = [];

    for (const pct of pricePcts) {
      const price = spot * pct / 100;
      const row: (string | number)[] = [`$${price.toFixed(0)} (${pct}%)`];
      for (const dte of surfaceDteSteps) {
        const h = 0.5;
        const pUp = estPrem(price + h, strike, iv, dte, 'put');
        const pDown = estPrem(price - h, strike, iv, dte, 'put');
        const delta = -(pUp - pDown) / (2 * h);
        const clamped = Math.max(-1, Math.min(0, delta));
        row.push(clamped.toFixed(2));
      }
      deltaBody.push(row);
    }

    autoTable(doc, {
      startY: y,
      head: deltaHead,
      body: deltaBody,
      theme: 'grid',
      styles: { fontSize: 6, cellPadding: 1, halign: 'center' },
      headStyles: { fillColor: [15, 23, 42], textColor: 255, fontStyle: 'bold', fontSize: 6 },
      columnStyles: { 0: { halign: 'left', fontStyle: 'bold', fontSize: 6.5 } },
      margin: { left: margin, right: margin },
      didParseCell: (data: any) => {
        if (data.section === 'body' && data.column.index >= 1) {
          const val = Math.abs(parseFloat(data.cell.raw as string));
          if (val > 0.7) {
            data.cell.styles.fillColor = [254, 202, 202]; // deep red
            data.cell.styles.fontStyle = 'bold';
          } else if (val > 0.4) {
            data.cell.styles.fillColor = [254, 240, 199]; // orange/yellow
          } else if (val > 0.15) {
            data.cell.styles.fillColor = [219, 234, 254]; // blue tint
          } else {
            data.cell.styles.fillColor = [220, 252, 231]; // green tint
          }
        }
        // Highlight spot row
        if (data.section === 'body') {
          const rowPct = pricePcts[data.row.index];
          if (rowPct === 100) {
            data.cell.styles.fontStyle = 'bold';
            if (data.column.index === 0) {
              data.cell.styles.textColor = [37, 99, 235];
            }
          }
        }
      },
    });
    y = (doc as any).lastAutoTable.finalY + 5;
  }

  doc.setFontSize(7);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(100);
  doc.text('Color scale: Green (δ≈0, OTM) → Blue (δ≈-0.2) → Yellow (δ≈-0.5, ATM) → Red (δ≈-1.0, deep ITM). Bold row = current spot price.', margin, y);
  doc.setTextColor(0);

  addFooter(pg.v);

  // ── Disclaimer ──
  doc.addPage();
  pg.v++;
  addHeader();
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100);
  const disclaimer = [
    'DISCLAIMER',
    '',
    'This report is generated for informational purposes only and does not constitute financial advice.',
    'Option premiums are estimated using simplified Black-Scholes approximations and may differ significantly',
    'from actual market prices. Always consult with a qualified financial advisor and verify option prices',
    'with your broker before executing any hedge strategy.',
    '',
    'USD/CHF conversion rate used: ' + USD_CHF.toFixed(4),
    'All values are estimates and subject to market conditions at time of execution.',
  ];
  disclaimer.forEach(line => {
    doc.text(line, margin, y);
    y += 5;
  });
  addFooter(pg.v);

  if (returnDoc) return doc;
  doc.save(`hedge-report-${new Date().toISOString().slice(0, 10)}.pdf`);
  return doc;
}

// ─── Capital Protection Chart ──────────────────────────────────────

function CapitalProtectionChart({ positions, totalPortfolioValue }: { positions: HedgePosition[]; totalPortfolioValue: number }) {
  const data = useMemo(() => {
    const scenarios = [];
    for (let dropPct = -30; dropPct <= 10; dropPct += 5) {
      const unhedgedValue = totalPortfolioValue * (1 + dropPct / 100);

      const row: any = {
        scenario: `${dropPct > 0 ? '+' : ''}${dropPct}%`,
        dropPct,
        unhedgedValue: Math.round(unhedgedValue),
        unhedgedValueCHF: Math.round(unhedgedValue * USD_CHF),
      };

      for (const tier of PROTECTION_TIERS) {
        let putPayoff = 0;
        let hedgeCost = 0;
        for (const p of positions) {
          const vol = estimateVolatility(p.gainPct);
          const strikePct = 1 - tier.otmPct;
          const strike = p.currentPrice * strikePct;
          const premium = estimateOptionPremium(p.currentPrice, strike, vol, tier.daysToExpiry, 'put');
          const contracts = Math.ceil(p.quantity / 100);
          hedgeCost += premium * contracts * 100;

          const newPrice = p.currentPrice * (1 + dropPct / 100);
          if (newPrice < strike) {
            putPayoff += (strike - newPrice) * contracts * 100;
          }
        }
        const protectedValue = unhedgedValue + putPayoff - hedgeCost;
        row[`${tier.id}_value`] = Math.round(protectedValue);
        row[`${tier.id}_valueCHF`] = Math.round(protectedValue * USD_CHF);
        row[`${tier.id}_saved`] = Math.round(protectedValue - unhedgedValue);
        row[`${tier.id}_pct`] = Math.round((protectedValue / totalPortfolioValue) * 10000) / 100;
      }

      scenarios.push(row);
    }
    return scenarios;
  }, [positions, totalPortfolioValue]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-popover border border-border rounded-xl shadow-xl p-4 min-w-[280px]">
        <p className="font-bold text-sm text-foreground mb-2">Market Move: {label}</p>
        <div className="space-y-2">
          {payload.map((p: any, i: number) => (
            <div key={i} className="flex justify-between items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: p.color }} />
                <span className="text-xs text-foreground">{p.name}</span>
              </div>
              <span className="text-xs font-bold font-mono" style={{ color: p.color }}>
                {fmt(p.value)}
              </span>
            </div>
          ))}
        </div>
        <div className="mt-2 pt-2 border-t border-border/50">
          <p className="text-[10px] text-muted-foreground">
            Portfolio: {fmt(totalPortfolioValue)} → {label} move
          </p>
        </div>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Shield className="h-4 w-4" />
          Capital Protected by Tier — Market Scenario Analysis
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Total portfolio value after hedging for each protection tier across market scenarios. Higher = more capital preserved.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <ResponsiveContainer width="100%" height={400}>
          <AreaChart data={data} margin={{ top: 10, right: 20, bottom: 25, left: 15 }}>
            <defs>
              {PROTECTION_TIERS.map(t => (
                <linearGradient key={t.id} id={`grad-${t.id}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={t.color} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={t.color} stopOpacity={0.03} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
            <XAxis
              dataKey="scenario"
              tick={{ fontSize: 11, fontWeight: 600 }}
              label={{ value: 'Market Move', position: 'insideBottom', offset: -15, style: { fontSize: 11 } }}
            />
            <YAxis
              tick={{ fontSize: 10 }}
              tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`}
              label={{ value: 'Portfolio Value (USD)', angle: -90, position: 'insideLeft', offset: 5, style: { fontSize: 10 } }}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine y={totalPortfolioValue} stroke="hsl(var(--muted-foreground))" strokeDasharray="6 3" strokeWidth={1.5} label={{ value: `Current: ${fmt(totalPortfolioValue)}`, position: 'right', style: { fontSize: 9, fill: 'hsl(var(--muted-foreground))' } }} />
            <Area type="monotone" dataKey="unhedgedValue" stroke="#94a3b8" fill="#94a3b8" fillOpacity={0.08} name="No Hedge" strokeWidth={2} strokeDasharray="5 3" />
            {PROTECTION_TIERS.map(t => (
              <Area
                key={t.id}
                type="monotone"
                dataKey={`${t.id}_value`}
                stroke={t.color}
                fill={`url(#grad-${t.id})`}
                name={t.label.split('(')[0].trim()}
                strokeWidth={2.5}
              />
            ))}
            <Legend
              wrapperStyle={{ fontSize: 11, paddingTop: 10 }}
              iconType="circle"
              iconSize={10}
            />
          </AreaChart>
        </ResponsiveContainer>

        {/* Summary table */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="border-b-2 border-border">
                <th className="text-left py-2 px-2 font-semibold text-foreground">Scenario</th>
                <th className="text-right py-2 px-2 font-semibold text-muted-foreground">No Hedge</th>
                {PROTECTION_TIERS.map(t => (
                  <th key={t.id} className="text-right py-2 px-2 font-semibold" style={{ color: t.color }}>
                    {t.label.split('(')[0].trim()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map(row => (
                <tr key={row.dropPct} className={`border-b border-border/30 ${row.dropPct === 0 ? 'bg-muted/20 font-semibold' : ''}`}>
                  <td className={`py-1.5 px-2 font-mono font-semibold ${row.dropPct < 0 ? 'text-destructive' : 'text-green-600 dark:text-green-400'}`}>
                    {row.scenario}
                  </td>
                  <td className="py-1.5 px-2 text-right font-mono text-muted-foreground">
                    {fmt(row.unhedgedValue)}
                    <span className="block text-[10px]">{fmt(row.unhedgedValueCHF, 'CHF')}</span>
                  </td>
                  {PROTECTION_TIERS.map(t => {
                    const val = row[`${t.id}_value`];
                    const saved = row[`${t.id}_saved`];
                    const pct = row[`${t.id}_pct`];
                    return (
                      <td key={t.id} className="py-1.5 px-2 text-right font-mono">
                        <span className="font-semibold" style={{ color: t.color }}>{fmt(val)}</span>
                        <span className="block text-[10px] text-muted-foreground">{fmt(row[`${t.id}_valueCHF`], 'CHF')}</span>
                        <span className={`block text-[10px] font-semibold ${saved > 0 ? 'text-green-600 dark:text-green-400' : saved < 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
                          {saved > 0 ? '+' : ''}{fmt(saved)} ({pct}%)
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground">
          <strong>% of portfolio</strong> = remaining capital as % of original value. Values include hedge cost deduction. <strong>Green amounts</strong> = capital saved vs. no hedge.
        </p>
      </CardContent>
    </Card>
  );
}

// ─── Main Component ────────────────────────────────────────────────

export function PortfolioHedgeSimulator({ analyses, positions, totalPortfolioValue }: Props) {
  const [selectedTierIdx, setSelectedTierIdx] = useState(1); // balanced default
  const [expDays, setExpDays] = useState(60);
  const [emailSending, setEmailSending] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('jbmeyerposition@gmail.com');
  const { toast } = useToast();

  const tier = PROTECTION_TIERS[selectedTierIdx];

  const costChartData = useMemo(() => generateCostVsDropData(positions, expDays), [positions, expDays]);
  const simData = useMemo(() => generateSimulationData(positions, tier.otmPct, expDays), [positions, tier, expDays]);
  const tierCosts = useMemo(() => PROTECTION_TIERS.map(t => ({ ...t, ...computeTierCost(positions, t) })), [positions]);
  const optionRecs = useMemo(() => generateOptionRecommendations(positions, tier), [positions, tier]);

  const selectedCost = tierCosts[selectedTierIdx];

  const handleExportPDF = useCallback(() => {
    generateHedgePDF(positions, totalPortfolioValue, tier, tierCosts, simData, optionRecs, analyses, selectedCost);
  }, [positions, totalPortfolioValue, tier, tierCosts, simData, optionRecs, analyses, selectedCost]);

  const handleEmailReport = useCallback(async () => {
    setEmailSending(true);
    try {
      const doc = generateHedgePDF(positions, totalPortfolioValue, tier, tierCosts, simData, optionRecs, analyses, selectedCost, true);
      const pdfBase64 = doc.output('datauristring').split(',')[1];

      // Compute Greeks data for email
      const computeGammaForEmail = (spot: number, strike: number, iv: number, dte: number) => {
        const h = 0.5;
        const pUp = estimateOptionPremium(spot + h, strike, iv, Math.max(dte, 0.5), 'put');
        const pMid = estimateOptionPremium(spot, strike, iv, Math.max(dte, 0.5), 'put');
        const pDown = estimateOptionPremium(spot - h, strike, iv, Math.max(dte, 0.5), 'put');
        return Math.abs((pUp - 2 * pMid + pDown) / (h * h));
      };

      const greeksData = analyses.map(a => {
        const pos = a.position;
        const spot = pos.currentPrice;
        const iv = estimateVolatility(pos.gainPct);
        const strikeAtm = spot;
        const strikeOtm5 = spot * 0.95;
        const vegaAtm = estimateOptionPremium(spot, strikeAtm, iv + 0.01, 90, 'put') - estimateOptionPremium(spot, strikeAtm, iv, 90, 'put');
        const vegaOtm5 = estimateOptionPremium(spot, strikeOtm5, iv + 0.01, 90, 'put') - estimateOptionPremium(spot, strikeOtm5, iv, 90, 'put');
        return {
          ticker: pos.ticker,
          spot,
          iv,
          vegaAtm,
          vegaOtm5,
          gammaAtm90d: computeGammaForEmail(spot, strikeAtm, iv, 90),
          gammaAtm30d: computeGammaForEmail(spot, strikeAtm, iv, 30),
          gammaAtm7d: computeGammaForEmail(spot, strikeAtm, iv, 7),
        };
      });

      const { error } = await supabase.functions.invoke('send-hedge-report', {
        body: {
          pdfBase64,
          recipientEmail,
          positionsCount: positions.length,
          totalValueUSD: Math.round(totalPortfolioValue),
          totalValueCHF: Math.round(toChf(totalPortfolioValue)),
          hedgeCostUSD: Math.round(selectedCost.totalCostUSD),
          tierLabel: tier.label,
          greeksData,
        },
      });

      if (error) throw error;

      toast({ title: 'Report Sent', description: `Hedge report emailed to ${recipientEmail}` });
    } catch (e: any) {
      console.error('Email send failed:', e);
      toast({ title: 'Email Failed', description: e.message || 'Could not send report', variant: 'destructive' });
    } finally {
      setEmailSending(false);
    }
  }, [positions, totalPortfolioValue, tier, tierCosts, simData, optionRecs, analyses, selectedCost, toast, recipientEmail]);

  return (
    <div className="space-y-6">
      {/* ─── Export Button + Header summary ─────────────────── */}
      <div className="flex justify-end gap-2 items-center">
        <Input
          type="email"
          placeholder="Recipient email"
          value={recipientEmail}
          onChange={(e) => setRecipientEmail(e.target.value)}
          className="max-w-[260px] h-9 text-sm"
        />
        <Button onClick={handleEmailReport} variant="outline" size="sm" className="gap-2" disabled={emailSending || !recipientEmail}>
          {emailSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
          Email Report
        </Button>
        <Button onClick={handleExportPDF} variant="outline" size="sm" className="gap-2">
          <FileDown className="h-4 w-4" />
          Export PDF Report
        </Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card><CardContent className="p-3 text-center">
          <p className="text-[10px] text-muted-foreground uppercase">Portfolio (USD)</p>
          <p className="text-lg font-bold text-foreground">{fmt(totalPortfolioValue)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <p className="text-[10px] text-muted-foreground uppercase">Portfolio (CHF)</p>
          <p className="text-lg font-bold text-foreground">{fmt(toChf(totalPortfolioValue), 'CHF')}</p>
        </CardContent></Card>
        <Card className="border-primary/30"><CardContent className="p-3 text-center">
          <p className="text-[10px] text-muted-foreground uppercase">Hedge Cost (USD)</p>
          <p className="text-lg font-bold text-destructive">{fmt(selectedCost.totalCostUSD)}</p>
          <p className="text-[10px] text-muted-foreground">{((selectedCost.totalCostUSD / totalPortfolioValue) * 100).toFixed(2)}%</p>
        </CardContent></Card>
        <Card className="border-primary/30"><CardContent className="p-3 text-center">
          <p className="text-[10px] text-muted-foreground uppercase">Hedge Cost (CHF)</p>
          <p className="text-lg font-bold text-destructive">{fmt(toChf(selectedCost.totalCostUSD), 'CHF')}</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <p className="text-[10px] text-muted-foreground uppercase">Hedged Value (USD)</p>
          <p className="text-lg font-bold text-primary">{fmt(costChartData.find(d => d.otm === Math.round(tier.otmPct * 100))?.hedgedUSD ?? 0)}</p>
        </CardContent></Card>
      </div>

      {/* ─── Tier Selector ──────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Target className="h-4 w-4" />
            Protection Tier & Expiry
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {PROTECTION_TIERS.map((t, i) => (
              <button
                key={t.id}
                onClick={() => { setSelectedTierIdx(i); setExpDays(t.daysToExpiry); }}
                className={`p-3 rounded-lg border text-left transition-all ${
                  i === selectedTierIdx
                    ? 'border-primary bg-primary/10 ring-1 ring-primary'
                    : 'border-border hover:border-muted-foreground/30'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: t.color }} />
                  <span className="text-xs font-semibold text-foreground">{t.label}</span>
                </div>
                <p className="text-[10px] text-muted-foreground">{t.description}</p>
                <p className="text-sm font-bold mt-1" style={{ color: t.color }}>
                  {fmt(tierCosts[i].totalCostUSD)} / {fmt(toChf(tierCosts[i].totalCostUSD), 'CHF')}
                </p>
              </button>
            ))}
          </div>
          <div className="max-w-xs space-y-1">
            <div className="flex justify-between">
              <Label className="text-xs text-muted-foreground">Days to Expiry</Label>
              <span className="text-xs font-mono font-bold text-primary">{expDays}d</span>
            </div>
            <Slider value={[expDays]} min={7} max={180} step={1} onValueChange={([v]) => setExpDays(v)} />
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="cost-chart" className="w-full">
        <TabsList className="flex flex-wrap gap-1 h-auto p-1">
          <TabsTrigger value="cost-chart" className="gap-1 text-xs"><BarChart3 className="h-3 w-3" />Cost vs Protection</TabsTrigger>
          <TabsTrigger value="simulation" className="gap-1 text-xs"><TrendingDown className="h-3 w-3" />Loss/Profit Sim</TabsTrigger>
          <TabsTrigger value="currency" className="gap-1 text-xs"><Coins className="h-3 w-3" />Currency</TabsTrigger>
          <TabsTrigger value="correlation" className="gap-1 text-xs"><Grid3x3 className="h-3 w-3" />Correlation</TabsTrigger>
          <TabsTrigger value="risk-heatmap" className="gap-1 text-xs"><Flame className="h-3 w-3" />Risk Heatmap</TabsTrigger>
          <TabsTrigger value="options" className="gap-1 text-xs"><Shield className="h-3 w-3" />Options to Buy</TabsTrigger>
          <TabsTrigger value="comparison" className="gap-1 text-xs"><DollarSign className="h-3 w-3" />Tier Comparison</TabsTrigger>
          <TabsTrigger value="capital-protection" className="gap-1 text-xs"><Shield className="h-3 w-3" />Capital Protected</TabsTrigger>
          <TabsTrigger value="index-hedge" className="gap-1 text-xs"><Globe className="h-3 w-3" />Index Hedge</TabsTrigger>
        </TabsList>

        {/* ─── Tab 1: Cost vs Protection Chart ─────────────── */}
        <TabsContent value="cost-chart" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Protection Cost & Hedged Amount vs. Strike Depth ({expDays}d expiry)</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={costChartData} margin={{ top: 10, right: 20, bottom: 20, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                  <XAxis dataKey="otm" tick={{ fontSize: 10 }} label={{ value: '% Out-of-the-Money', position: 'insideBottom', offset: -10, style: { fontSize: 11 } }} />
                  <YAxis yAxisId="cost" tick={{ fontSize: 10 }} tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`} label={{ value: 'Cost ($)', angle: -90, position: 'insideLeft', style: { fontSize: 10 } }} />
                  <YAxis yAxisId="hedged" orientation="right" tick={{ fontSize: 10 }} tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`} label={{ value: 'Hedged Value ($)', angle: 90, position: 'insideRight', style: { fontSize: 10 } }} />
                  <Tooltip content={<CostTooltip />} />
                  <ReferenceLine x={Math.round(tier.otmPct * 100)} stroke="hsl(var(--primary))" strokeDasharray="5 3" strokeWidth={2} yAxisId="cost" label={{ value: tier.label.split('(')[0], style: { fontSize: 9, fill: 'hsl(var(--primary))' } }} />
                  <Line yAxisId="cost" type="monotone" dataKey="costUSD" stroke="#ef4444" name="Cost (USD)" strokeWidth={2.5} dot={false} />
                  <Line yAxisId="cost" type="monotone" dataKey="costCHF" stroke="#f97316" name="Cost (CHF)" strokeWidth={2} strokeDasharray="4 2" dot={false} />
                  <Line yAxisId="hedged" type="monotone" dataKey="hedgedUSD" stroke="#3b82f6" name="Hedged (USD)" strokeWidth={2} dot={false} />
                  <Line yAxisId="hedged" type="monotone" dataKey="hedgedCHF" stroke="#06b6d4" name="Hedged (CHF)" strokeWidth={2} strokeDasharray="4 2" dot={false} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                </LineChart>
              </ResponsiveContainer>
              <p className="text-xs text-muted-foreground mt-2">
                Move cursor over the chart to see exact cost and hedged amounts at each strike depth. Current tier: <strong>{tier.label}</strong>.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Tab 2: Loss/Profit Simulation Table ─────────── */}
        <TabsContent value="simulation" className="mt-4 space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Market Move Simulation — {tier.label}</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Chart */}
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={simData} margin={{ top: 10, right: 20, bottom: 20, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                  <XAxis dataKey="marketMove" tick={{ fontSize: 10 }} tickFormatter={(v: number) => `${v > 0 ? '+' : ''}${v}%`} label={{ value: 'Market Move', position: 'insideBottom', offset: -10, style: { fontSize: 11 } }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v: number) => fmt(v)} labelFormatter={(l: number) => `Market ${l > 0 ? '+' : ''}${l}%`} contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                  <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" />
                  <ReferenceLine x={0} stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" />
                  <Area type="monotone" dataKey="unhedgedPL_USD" stroke="#94a3b8" fill="#94a3b8" fillOpacity={0.1} name="Unhedged P&L" strokeWidth={2} />
                  <Area type="monotone" dataKey="hedgedPL_USD" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.15} name="Hedged P&L" strokeWidth={2.5} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                </AreaChart>
              </ResponsiveContainer>

              {/* Table */}
              <div className="overflow-x-auto mt-4">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="border-b-2 border-border">
                      <th className="text-left py-2 px-2 font-semibold text-foreground">Market</th>
                      <th className="text-right py-2 px-2 font-semibold text-foreground">Unhedged (USD)</th>
                      <th className="text-right py-2 px-2 font-semibold text-foreground">Unhedged (CHF)</th>
                      <th className="text-right py-2 px-2 font-semibold text-foreground">Put Payoff</th>
                      <th className="text-right py-2 px-2 font-semibold text-foreground">Hedge Cost</th>
                      <th className="text-right py-2 px-2 font-semibold text-foreground">Hedged P&L (USD)</th>
                      <th className="text-right py-2 px-2 font-semibold text-foreground">Hedged P&L (CHF)</th>
                      <th className="text-right py-2 px-2 font-semibold text-foreground">Net Benefit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {simData.map((row) => {
                      const isNeg = row.marketMove < 0;
                      const isZero = row.marketMove === 0;
                      return (
                        <tr key={row.marketMove} className={`border-b border-border/30 ${isZero ? 'bg-muted/20 font-semibold' : ''}`}>
                          <td className={`py-1.5 px-2 font-mono ${isNeg ? 'text-destructive' : 'text-green-600 dark:text-green-400'}`}>
                            {row.marketMove > 0 ? '+' : ''}{row.marketMove}%
                          </td>
                          <td className={`py-1.5 px-2 text-right font-mono ${row.unhedgedPL_USD < 0 ? 'text-destructive' : 'text-green-600 dark:text-green-400'}`}>
                            {fmt(row.unhedgedPL_USD)}
                          </td>
                          <td className={`py-1.5 px-2 text-right font-mono ${row.unhedgedPL_CHF < 0 ? 'text-destructive' : 'text-green-600 dark:text-green-400'}`}>
                            {fmt(row.unhedgedPL_CHF, 'CHF')}
                          </td>
                          <td className="py-1.5 px-2 text-right font-mono text-primary">
                            {row.putPayoff_USD > 0 ? fmt(row.putPayoff_USD) : '—'}
                          </td>
                          <td className="py-1.5 px-2 text-right font-mono text-destructive">
                            {fmt(row.hedgeCost_USD)}
                          </td>
                          <td className={`py-1.5 px-2 text-right font-mono font-semibold ${row.hedgedPL_USD < 0 ? 'text-destructive' : 'text-green-600 dark:text-green-400'}`}>
                            {fmt(row.hedgedPL_USD)}
                          </td>
                          <td className={`py-1.5 px-2 text-right font-mono ${row.hedgedPL_CHF < 0 ? 'text-destructive' : 'text-green-600 dark:text-green-400'}`}>
                            {fmt(row.hedgedPL_CHF, 'CHF')}
                          </td>
                          <td className={`py-1.5 px-2 text-right font-mono font-bold ${row.netBenefit_USD > 0 ? 'text-green-600 dark:text-green-400' : row.netBenefit_USD < 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
                            {fmt(row.netBenefit_USD)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                <strong>Net Benefit</strong> = Hedged P&L − Unhedged P&L. Positive means the hedge saved money vs. no protection.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Tab 3: Options to Buy ───────────────────────── */}
        <TabsContent value="options" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Recommended Put Options — {tier.label}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="border-b-2 border-border">
                      <th className="text-left py-2 px-2 font-semibold text-foreground">Ticker</th>
                      <th className="text-left py-2 px-2 font-semibold text-foreground">Name</th>
                      <th className="text-right py-2 px-2 font-semibold text-foreground">Spot</th>
                      <th className="text-right py-2 px-2 font-semibold text-foreground">Qty</th>
                      <th className="text-center py-2 px-2 font-semibold text-foreground">Type</th>
                      <th className="text-right py-2 px-2 font-semibold text-foreground">Strike</th>
                      <th className="text-right py-2 px-2 font-semibold text-foreground">Expiry</th>
                      <th className="text-right py-2 px-2 font-semibold text-foreground">Prem/sh</th>
                      <th className="text-right py-2 px-2 font-semibold text-foreground">Contracts</th>
                      <th className="text-right py-2 px-2 font-semibold text-foreground">Total Cost</th>
                      <th className="text-right py-2 px-2 font-semibold text-foreground">Deductible</th>
                    </tr>
                  </thead>
                  <tbody>
                    {optionRecs.map((r) => (
                      <tr key={r.ticker} className="border-b border-border/30 hover:bg-muted/20">
                        <td className="py-1.5 px-2 font-bold text-foreground">{r.ticker}</td>
                        <td className="py-1.5 px-2 text-muted-foreground max-w-[120px] truncate">{r.stockName}</td>
                        <td className="py-1.5 px-2 text-right font-mono">{fmt(r.currentPrice)}</td>
                        <td className="py-1.5 px-2 text-right">{r.quantity}</td>
                        <td className="py-1.5 px-2 text-center">
                          <Badge variant="outline" className="bg-red-500/10 text-red-700 dark:text-red-400 text-[10px]">PUT</Badge>
                        </td>
                        <td className="py-1.5 px-2 text-right font-mono font-semibold">{fmt(r.strike)}</td>
                        <td className="py-1.5 px-2 text-right">{r.expiry}</td>
                        <td className="py-1.5 px-2 text-right font-mono">{fmt(r.premiumPerShare)}</td>
                        <td className="py-1.5 px-2 text-right">{r.contracts}</td>
                        <td className="py-1.5 px-2 text-right font-mono text-destructive font-semibold">{fmt(r.totalCost)}</td>
                        <td className="py-1.5 px-2 text-right font-mono text-muted-foreground">{fmt(r.deductible)}</td>
                      </tr>
                    ))}
                    <tr className="border-t-2 border-border bg-muted/20 font-bold">
                      <td colSpan={9} className="py-2 px-2 text-right text-foreground">Total Hedge Cost:</td>
                      <td className="py-2 px-2 text-right font-mono text-destructive">{fmt(selectedCost.totalCostUSD)}</td>
                      <td className="py-2 px-2 text-right font-mono text-muted-foreground">{fmt(toChf(selectedCost.totalCostUSD), 'CHF')}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Tab 4: Tier Comparison ──────────────────────── */}
        <TabsContent value="comparison" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">All Tiers — Cost & Deductible Comparison</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={tierCosts.map(t => ({
                  name: t.label.split('(')[0].trim(),
                  costUSD: t.totalCostUSD,
                  costCHF: Math.round(t.totalCostUSD * USD_CHF),
                  pctOfPortfolio: Math.round((t.totalCostUSD / totalPortfolioValue) * 10000) / 100,
                }))} margin={{ top: 10, right: 20, bottom: 20, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                  <XAxis dataKey="name" tick={{ fontSize: 9 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} formatter={(v: number, n: string) => [n.includes('CHF') ? fmt(v, 'CHF') : n.includes('%') ? `${v}%` : fmt(v), n]} />
                  <Bar dataKey="costUSD" name="Cost (USD)">
                    {PROTECTION_TIERS.map((t, i) => (
                      <Cell key={i} fill={t.color} fillOpacity={i === selectedTierIdx ? 1 : 0.4} />
                    ))}
                  </Bar>
                  <Bar dataKey="costCHF" name="Cost (CHF)">
                    {PROTECTION_TIERS.map((t, i) => (
                      <Cell key={i} fill={t.color} fillOpacity={i === selectedTierIdx ? 0.7 : 0.25} />
                    ))}
                  </Bar>
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                </BarChart>
              </ResponsiveContainer>

              <div className="overflow-x-auto mt-4">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="border-b-2 border-border">
                      <th className="text-left py-2 px-2 font-semibold text-foreground">Tier</th>
                      <th className="text-right py-2 px-2 font-semibold text-foreground">OTM %</th>
                      <th className="text-right py-2 px-2 font-semibold text-foreground">Expiry</th>
                      <th className="text-right py-2 px-2 font-semibold text-foreground">Cost (USD)</th>
                      <th className="text-right py-2 px-2 font-semibold text-foreground">Cost (CHF)</th>
                      <th className="text-right py-2 px-2 font-semibold text-foreground">% of Portfolio</th>
                      <th className="text-right py-2 px-2 font-semibold text-foreground">Deductible</th>
                    </tr>
                  </thead>
                  <tbody>
                    {PROTECTION_TIERS.map((t, i) => (
                      <tr key={t.id} className={`border-b border-border/30 ${i === selectedTierIdx ? 'bg-primary/5 font-semibold' : ''}`}>
                        <td className="py-1.5 px-2">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: t.color }} />
                            <span className="text-foreground">{t.label.split('(')[0].trim()}</span>
                          </div>
                        </td>
                        <td className="py-1.5 px-2 text-right font-mono">{t.otmPct * 100}%</td>
                        <td className="py-1.5 px-2 text-right">{t.daysToExpiry}d</td>
                        <td className="py-1.5 px-2 text-right font-mono text-destructive">{fmt(tierCosts[i].totalCostUSD)}</td>
                        <td className="py-1.5 px-2 text-right font-mono text-destructive">{fmt(toChf(tierCosts[i].totalCostUSD), 'CHF')}</td>
                        <td className="py-1.5 px-2 text-right font-mono">{((tierCosts[i].totalCostUSD / totalPortfolioValue) * 100).toFixed(2)}%</td>
                        <td className="py-1.5 px-2 text-right text-muted-foreground">{t.otmPct * 100}% before protection</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Tab: Currency Exposure ──────────────────────── */}
        <TabsContent value="currency" className="mt-4">
          <CurrencyExposure positions={positions} totalPortfolioValue={totalPortfolioValue} />
        </TabsContent>

        {/* ─── Tab: Correlation Matrix ─────────────────────── */}
        <TabsContent value="correlation" className="mt-4">
          <CorrelationMatrix positions={positions} />
        </TabsContent>

        {/* ─── Tab 5: Risk Heatmap ─────────────────────────── */}
        <TabsContent value="risk-heatmap" className="mt-4 space-y-4">
          <RiskHeatmap analyses={analyses} positions={positions} totalPortfolioValue={totalPortfolioValue} tier={tier} />
        </TabsContent>

        {/* ─── Tab: Capital Protection Overview ─────────────── */}
        <TabsContent value="capital-protection" className="mt-4">
          <CapitalProtectionChart positions={positions} totalPortfolioValue={totalPortfolioValue} />
        </TabsContent>

        {/* ─── Tab: Index Hedge ─────────────────────────────── */}
        <TabsContent value="index-hedge" className="mt-4">
          <IndexHedgePanel positions={positions} totalPortfolioValue={totalPortfolioValue} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
